import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Copy,
  Check,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Shield,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";

import "../styles/responsive/pages/PaymentPage.css";

import { formatCrypto, formatUSD } from "../utils/formatAmount";
import { addAndSwitchNetwork } from "../utils/web3Utils";
import AssetSelect from "../components/common/AssetSelect";

import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
} from "@reown/appkit/react";
import {
  useWriteContract,
  useWaitForTransactionReceipt,
  useSwitchChain,
  useSendTransaction,
} from "wagmi";
import { parseUnits, encodeFunctionData } from "viem";
import imglogo from "../assets/maxelpay-logo-light.svg";
// Dark-ink variant, for the footer, which sits on white. The -light file is
// the white-on-dark artwork and would be invisible there.
import imglogoDark from "../assets/maxelpay-logo-dark.svg";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const PaymentPage = () => {
  const { linkId, sessionId } = useParams();
  const location = useLocation();

  const { open: openWalletModal } = useAppKit();
  const { address: walletAddress, isConnected } = useAppKitAccount();
  const { chainId: currentChainId } = useAppKitNetwork();
  const { switchChain } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();

  const [pendingTxHash, setPendingTxHash] = useState(null);
  const [isWalletProcessing, setIsWalletProcessing] = useState(false);

  const isInvoice = location.pathname.includes("/pay/invoice/");
  const isSessionPayment = location.pathname.includes("/pay/session/");

  const [paymentLink, setPaymentLink] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const [isApiKeyPayment, setIsApiKeyPayment] = useState(false);
  const [createdPaymentLinkId, setCreatedPaymentLinkId] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const urlAmount = searchParams.get("amount");
  const urlCurrency = searchParams.get("currency");
  const urlSignature = searchParams.get("sig");
  const urlReturnUrl = searchParams.get("return_url");

  const [networks, setNetworks] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [allSessionTokens, setAllSessionTokens] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedToken, setSelectedToken] = useState("");
  const [isLoadingNetworks, setIsLoadingNetworks] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);

  const [paymentSession, setPaymentSession] = useState(null);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [isReleasingSession, setIsReleasingSession] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);

  const [activePaymentTab, setActivePaymentTab] = useState("qr");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paidDetails, setPaidDetails] = useState(null);

  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState("");

  const [confirmationStatus, setConfirmationStatus] = useState(null);
  const [blocksConfirmed, setBlocksConfirmed] = useState(0);
  const [requiredConfirmations, setRequiredConfirmations] = useState(0);

  const timerRef = useRef(null);
  const statusPollingRef = useRef(null);
  const confirmationPollingRef = useRef(null);

  const initialAmountPaidRef = useRef(null);

  const isPayRemainingFlowRef = useRef(false);

  const lastKnownUnsupportedTxRef = useRef(null);

  const referrerUrl = useRef(document.referrer || null);

  const autoSwitchAttemptedRef = useRef(null); // Track session + chainId for auto-switch to prevent duplicates

  // const generateEIP681Uri = useCallback((receivingAddress, tokenAmount, networkDetails, tokenDetails) => {
  //   if (!receivingAddress) return '';

  //   if (!tokenDetails?.contractAddress || tokenDetails?.isNative) {
  //     const decimals = tokenDetails?.decimals || 18;
  //     const amountInWei = tokenAmount ? BigInt(Math.floor(tokenAmount * 10 ** decimals)).toString() : '0';
  //     let uri = `ethereum:${receivingAddress}`;
  //     if (tokenAmount) {
  //       uri += `?value=${amountInWei}`;
  //     }
  //     if (networkDetails?.chainId) {
  //       uri += `${tokenAmount ? '&' : '?'}chain_id=${networkDetails.chainId}`;
  //     }
  //     return uri;
  //   }

  //   const decimals = tokenDetails?.decimals || 18;
  //   const amountInTokenUnits = tokenAmount ? BigInt(Math.floor(tokenAmount * 10 ** decimals)).toString() : '0';
  //   let uri = `ethereum:${tokenDetails.contractAddress}/transfer?address=${receivingAddress}&uint256=${amountInTokenUnits}`;
  //   if (networkDetails?.chainId) {
  //     uri += `&chain_id=${networkDetails.chainId}`;
  //   }
  //   return uri;
  // }, []);

  const generateEIP681Uri = useCallback(
    (receivingAddress, tokenAmount, networkDetails, tokenDetails) => {
      if (!receivingAddress) return "";

      const chainId = networkDetails?.chainId;
      const chainPart = chainId ? `@${chainId}` : "";

      if (!tokenDetails?.contractAddress || tokenDetails?.isNative) {
        const decimals = tokenDetails?.decimals || 18;
        const amountInWei = tokenAmount
          ? BigInt(Math.floor(tokenAmount * 10 ** decimals)).toString()
          : "0";

        let uri = `ethereum:${receivingAddress}${chainPart}`;

        if (tokenAmount) {
          uri += `?value=${amountInWei}`;
        }

        return uri;
      }

      const decimals = tokenDetails?.decimals || 18;
      const amountInTokenUnits = tokenAmount
        ? BigInt(Math.floor(tokenAmount * 10 ** decimals)).toString()
        : "0";

      let uri = `ethereum:${tokenDetails.contractAddress}${chainPart}/transfer?address=${receivingAddress}&uint256=${amountInTokenUnits}`;

      return uri;
    },
    [],
  );

  const fetchPaymentLink = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isSessionPayment) {
        const response = await fetch(
          `${API_BASE_URL}/v1/payments/sessions/${sessionId}`,
        );
        const data = await response.json();

        if (data.success) {
          const sessionData = data.data;

          const isPartiallyPaid =
            sessionData.status === "partially_paid" ||
            sessionData.isPartiallyPaid ||
            (parseFloat(sessionData.totalPaidUsd || 0) > 0 &&
              parseFloat(sessionData.remainingAmount || 0) > 0);

          const isUnsupportedTokenStatus =
            sessionData.status === "paid_unsupported_token";

          // Payment sessions are single-use: if any payment received, session is complete
          // Don't set pay-remaining flow - just show the payment status
          if (isPartiallyPaid || isUnsupportedTokenStatus) {
            // Don't enable pay-remaining flow for sessions - they're single-use
            isPayRemainingFlowRef.current = false;
            initialAmountPaidRef.current = parseFloat(
              sessionData.totalPaidUsd || 0,
            );
            console.log(
              "[fetchPaymentLink] Payment session has received payment and is now complete (single-use).",
            );
          }

          const effectiveAmount = sessionData.isPartiallyPaid
            ? sessionData.remainingAmount
            : sessionData.amount;

          setPaymentLink({
            id: sessionData.sessionId,
            amount: sessionData.amount,
            effectiveAmount: effectiveAmount,
            currency: sessionData.currency,
            name: sessionData.description,
            status: sessionData.status,
            successUrl: sessionData.successUrl,
            cancelUrl: sessionData.cancelUrl,
            customerEmail: sessionData.customerEmail,
            customerName: sessionData.customerName,
            isTestnet: sessionData.isTestnet,
            expiresAt: sessionData.expiresAt,
            totalPaidUsd: sessionData.totalPaidUsd || 0,
            remainingAmount: sessionData.remainingAmount || sessionData.amount,
            isPartiallyPaid: sessionData.isPartiallyPaid || false,
            receivingAddress: sessionData.receivingAddress,
            networkId: sessionData.networkId,
            tokenId: sessionData.tokenId,
            tokenAmount: sessionData.tokenAmount,
          });

          if (sessionData.networks) {
            setNetworks(sessionData.networks);
          }
          if (sessionData.tokens) {
            setAllSessionTokens(sessionData.tokens);
          }

          // Only auto-restore session if:
          // 1. Has receivingAddress
          // 2. NOT in pay-remaining flow (no partial payment or unsupported token)
          // 3. NOT in unsupported token status (user should select again)
          const shouldAutoRestoreSession =
            sessionData.receivingAddress &&
            !isPayRemainingFlowRef.current &&
            !isUnsupportedTokenStatus;

          if (shouldAutoRestoreSession) {
            initialAmountPaidRef.current = 0;
            sessionInitiatedRef.current = true;
            setPaymentSession({
              receivingAddress: sessionData.receivingAddress,
              tokenAmount: sessionData.tokenAmount,
              network: sessionData.networks?.find(
                (n) => n.id === sessionData.networkId,
              ),
              token: sessionData.tokens?.find(
                (t) => t.id === sessionData.tokenId,
              ),
            });
            setSelectedNetwork(sessionData.networkId?.toString() || "");
            setSelectedToken(sessionData.tokenId?.toString() || "");

            const SESSION_DURATION_MS = 15 * 60 * 1000;
            if (sessionData.sessionExpiresAt) {
              setSessionExpiresAt(sessionData.sessionExpiresAt);
            } else if (sessionData.timeRemainingMs) {
              setSessionExpiresAt(
                new Date(
                  Date.now() + sessionData.timeRemainingMs,
                ).toISOString(),
              );
            } else {
              setSessionExpiresAt(
                new Date(Date.now() + SESSION_DURATION_MS).toISOString(),
              );
            }

            const network = sessionData.networks?.find(
              (n) => n.id === sessionData.networkId,
            );
            const token = sessionData.tokens?.find(
              (t) => t.id === sessionData.tokenId,
            );
            const eip681Uri = generateEIP681Uri(
              sessionData.receivingAddress,
              sessionData.tokenAmount,
              network,
              token,
            );
            if (eip681Uri) {
              const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(eip681Uri)}`;
              setQrCodeUrl(qrUrl);
            }
          }
        } else {
          setError(data.message || "Payment session not found");
          setErrorCode(
            data.errorCode ||
              (data.status === "expired" ? "LINK_EXPIRED" : null),
          );
        }
        return;
      }

      if (isInvoice) {
        const response = await fetch(
          `${API_BASE_URL}/invoices/public/${linkId}`,
        );
        const data = await response.json();
        handlePaymentLinkResponse(data, false);
        return;
      }

      let apiKeyUrl = `${API_BASE_URL}/api-keys/public/${linkId}`;
      const queryParams = [];
      if (urlAmount)
        queryParams.push(`amount=${encodeURIComponent(urlAmount)}`);
      if (urlCurrency)
        queryParams.push(`currency=${encodeURIComponent(urlCurrency)}`);
      if (urlSignature)
        queryParams.push(`sig=${encodeURIComponent(urlSignature)}`);
      if (queryParams.length > 0) {
        apiKeyUrl += `?${queryParams.join("&")}`;
      }

      let response = await fetch(apiKeyUrl);
      let data = await response.json();

      if (data.success) {
        setIsApiKeyPayment(true);
        setPaymentLink(data.data);
        return;
      }

      if (response.status === 404) {
        response = await fetch(
          `${API_BASE_URL}/payment-links/public/${linkId}`,
        );
        data = await response.json();
        handlePaymentLinkResponse(data, false);
        return;
      }

      setError(data.message || "Payment link not found");
      setErrorCode(data.errorCode);
      if (data.data) {
        setPaymentLink(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch payment link:", err);
      setError("Failed to load payment link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    linkId,
    sessionId,
    isInvoice,
    isSessionPayment,
    urlAmount,
    urlCurrency,
    urlSignature,
    generateEIP681Uri,
  ]);

  const handlePaymentLinkResponse = useCallback(
    (data, isApiKey) => {
      if (data.success) {
        setPaymentLink(data.data);
        setIsApiKeyPayment(isApiKey);

        const isPartiallyPaid =
          data.data.status === "partially_paid" ||
          data.data.isPartialPayment ||
          (parseFloat(data.data.amountPaid || data.data.amount_paid || 0) > 0 &&
            parseFloat(
              data.data.amountRemaining || data.data.amount_remaining || 0,
            ) > 0);

        const isUnsupportedTokenStatus =
          data.data.status === "paid_unsupported_token";

        if (isPartiallyPaid || isUnsupportedTokenStatus) {
          isPayRemainingFlowRef.current = true;
          initialAmountPaidRef.current = parseFloat(
            data.data.amountPaid || data.data.amount_paid || 0,
          );
          if (
            isUnsupportedTokenStatus &&
            data.data.latestTransaction?.transactionId
          ) {
            lastKnownUnsupportedTxRef.current =
              data.data.latestTransaction.transactionId;
          }
          console.log(
            "[handlePaymentLinkResponse] Pay remaining flow detected (partially_paid or unsupported_token). Setting isPayRemainingFlowRef=true, initialAmountPaid:",
            initialAmountPaidRef.current,
          );
        }

        console.log("[handlePaymentLinkResponse] Received data:", {
          receivingAddress: data.data.receivingAddress,
          sessionTimeRemainingMs: data.data.sessionTimeRemainingMs,
          sessionExpiresAt: data.data.sessionExpiresAt,
          hasReceivingAddress: !!data.data.receivingAddress,
          hasValidSession: data.data.sessionTimeRemainingMs > 0,
          status: data.data.status,
          isPartiallyPaid,
        });

        const shouldRestoreSession =
          !isApiKey &&
          data.data.receivingAddress &&
          data.data.sessionTimeRemainingMs > 0 &&
          !isPayRemainingFlowRef.current;

        if (shouldRestoreSession) {
          initialAmountPaidRef.current = parseFloat(
            data.data.amount_paid || data.data.amountPaid || 0,
          );
          sessionInitiatedRef.current = true;
          console.log(
            "[PaymentPage] Session restored. Initial amount paid:",
            initialAmountPaidRef.current,
          );

          setPaymentSession({
            receivingAddress: data.data.receivingAddress,
            tokenAmount: data.data.tokenAmount,
            sessionExpiresAt: data.data.sessionExpiresAt,
            network: data.data.sessionNetworkDetails,
            token: data.data.sessionTokenDetails,
          });

          const SESSION_DURATION_MS = 15 * 60 * 1000;
          if (data.data.sessionExpiresAt) {
            setSessionExpiresAt(data.data.sessionExpiresAt);
          } else if (
            data.data.sessionTimeRemainingMs &&
            data.data.sessionTimeRemainingMs > 0
          ) {
            setSessionExpiresAt(
              new Date(
                Date.now() + data.data.sessionTimeRemainingMs,
              ).toISOString(),
            );
          } else {
            setSessionExpiresAt(
              new Date(Date.now() + SESSION_DURATION_MS).toISOString(),
            );
          }

          if (data.data.sessionNetworkDetails?.id) {
            setSelectedNetwork(data.data.sessionNetworkDetails.id.toString());
          }
          if (data.data.sessionTokenDetails?.id) {
            setSelectedToken(data.data.sessionTokenDetails.id.toString());
          }

          const eip681Uri = generateEIP681Uri(
            data.data.receivingAddress,
            data.data.tokenAmount,
            data.data.sessionNetworkDetails,
            data.data.sessionTokenDetails,
          );

          console.log("eip681Uri:", eip681Uri);

          if (eip681Uri) {
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(eip681Uri)}`;
            setQrCodeUrl(qrUrl);
          }
        }
      } else {
        setError(data.message || "Payment link not found");
        setErrorCode(data.errorCode);
        if (data.data) {
          setPaymentLink(data.data);
        }
      }
    },
    [generateEIP681Uri],
  );

  const fetchNetworks = useCallback(async () => {
    setIsLoadingNetworks(true);
    try {
      let response;

      if (isInvoice && linkId) {
        response = await fetch(
          `${API_BASE_URL}/invoices/public/${linkId}/payment-options`,
        );
        const data = await response.json();
        if (data.success && data.data) {
          setNetworks(data.data.networks || []);
          setAllSessionTokens(data.data.tokens || []);
        }
      } else if (linkId) {
        response = await fetch(
          `${API_BASE_URL}/payment-links/public/${linkId}/payment-options`,
        );
        const data = await response.json();
        if (data.success && data.data) {
          setNetworks(data.data.networks || []);
          setAllSessionTokens(data.data.tokens || []);
        }
      } else {
        response = await fetch(`${API_BASE_URL}/networks/active`);
        const data = await response.json();
        if (data.success && data.data) {
          setNetworks(data.data);
        }
      }
    } catch (err) {
      console.error("Failed to fetch networks:", err);
    } finally {
      setIsLoadingNetworks(false);
    }
  }, [isInvoice, linkId]);

  const fetchTokens = useCallback(async (networkId) => {
    if (!networkId) {
      setTokens([]);
      setSelectedToken("");
      return;
    }

    setIsLoadingTokens(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tokens/network/${networkId}`,
      );
      const data = await response.json();
      if (data.success && data.data) {
        setTokens(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch tokens:", err);
    } finally {
      setIsLoadingTokens(false);
    }
  }, []);

  const sessionInitiatedRef = useRef(false);

  useEffect(() => {
    setPaymentSuccess(false);
    setPaidDetails(null);
    sessionInitiatedRef.current = false;

    fetchPaymentLink();

    if (!isSessionPayment) {
      fetchNetworks();
    }
  }, [fetchPaymentLink, fetchNetworks, isSessionPayment]);

  useEffect(() => {
    if (!selectedNetwork) {
      setTokens([]);
      setSelectedToken("");
      return;
    }

    if (allSessionTokens.length > 0) {
      const filteredTokens = allSessionTokens.filter(
        (token) => token.networkId === parseInt(selectedNetwork),
      );
      setTokens(filteredTokens);

      setSelectedToken((prev) => {
        const isValidToken = filteredTokens.some(
          (t) => t.id.toString() === prev,
        );
        return isValidToken ? prev : "";
      });
    } else {
      fetchTokens(selectedNetwork);
    }
  }, [selectedNetwork, allSessionTokens, fetchTokens]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (!sessionExpiresAt) {
      return;
    }

    const calculateRemaining = () => {
      const now = Date.now();
      const expiresAtMs = new Date(sessionExpiresAt).getTime();
      return Math.max(0, expiresAtMs - now);
    };

    const initialRemaining = calculateRemaining();
    setSessionTimeRemaining(initialRemaining);

    if (initialRemaining <= 0) {
      setPaymentSession(null);
      setQrCodeUrl("");
      return;
    }

    timerRef.current = setInterval(() => {
      const remaining = calculateRemaining();
      setSessionTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setPaymentSession(null);
        setQrCodeUrl("");
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionExpiresAt]);

  useEffect(() => {
    // For invoices/payment links that already have payments (partial or unsupported token),
    // we need to poll even without an active payment session to detect new payments
    const hasExistingPayments =
      isPayRemainingFlowRef.current &&
      initialAmountPaidRef.current !== null &&
      initialAmountPaidRef.current > 0;

    // Don't start polling if there's no active payment session AND no existing payments to track
    if (!paymentSession && !hasExistingPayments) {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
      return;
    }

    // For pay-remaining flows with existing payments, always allow polling to detect new payments
    if (hasExistingPayments) {
      console.log(
        "[PaymentPage] Existing payments detected. Starting polling to detect new payments. Initial amount:",
        initialAmountPaidRef.current,
      );
      // Proceed to start polling
    } else if (paymentSession) {
      // For active payment sessions, check if polling should be allowed
      const shouldAllowPolling =
        sessionInitiatedRef.current ||
        isPayRemainingFlowRef.current ||
        paymentSession?.receivingAddress;

      if (!shouldAllowPolling) {
        console.log(
          "[PaymentPage] Session exists but was not initiated in this visit. Skipping polling.",
        );
        return;
      }
    }

    // Initialize the initial amount paid reference if not already set
    if (initialAmountPaidRef.current === null) {
      initialAmountPaidRef.current = parseFloat(
        paymentLink?.amountPaid || paymentLink?.amount_paid || 0,
      );
      console.log(
        "[PaymentPage] Initial amount paid set to:",
        initialAmountPaidRef.current,
      );
    }

    const checkPaymentStatus = async () => {
      try {
        let apiEndpoint;

        if (isSessionPayment) {
          apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/status`;
        } else if (isInvoice) {
          apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}`;
        } else if (isApiKeyPayment && createdPaymentLinkId) {
          apiEndpoint = `${API_BASE_URL}/api-keys/public/verify/${createdPaymentLinkId}`;
        } else {
          apiEndpoint = `${API_BASE_URL}/payment-links/public/${linkId}`;
        }

        const response = await fetch(apiEndpoint);
        const data = await response.json();

        if (isSessionPayment) {
          const sessionStatus = data.data?.status;
          const sessionData = data.data;

          if (
            [
              "paid",
              "partially_paid",
              "overpaid",
              "paid_unsupported_token",
            ].includes(sessionStatus)
          ) {
            const currentPaidAmount = parseFloat(
              sessionData?.totalPaidUsd ||
                sessionData?.total_paid_usd ||
                sessionData?.paidAmount ||
                0,
            );
            const initialPaid = parseFloat(initialAmountPaidRef.current || 0);
            const hasNewPayment = currentPaidAmount > initialPaid;
            const isPayRemainingMode = isPayRemainingFlowRef.current;

            // For unsupported tokens, check if this is a new transaction
            // Check both session level status AND transaction level flag
            const txData =
              sessionData?.latestTransaction ||
              sessionData?.transactions?.[0] ||
              {};
            const hasUnsupportedTokenAtSessionLevel =
              sessionStatus === "paid_unsupported_token";
            const hasUnsupportedTokenAtTxLevel =
              txData.isUnsupportedToken === true ||
              txData.is_unsupported_token === true ||
              txData.status === "not_supported_token";

            const isNewUnsupportedTokenPayment =
              (hasUnsupportedTokenAtSessionLevel ||
                hasUnsupportedTokenAtTxLevel) &&
              txData.transactionId &&
              txData.transactionId !== lastKnownUnsupportedTxRef.current;

            // Only trigger success if:
            // 1. There's a new payment (amount increased), OR
            // 2. New unsupported token payment, OR
            // 3. First-time detection (not in pay-remaining mode)
            const shouldTriggerSessionSuccess =
              hasNewPayment ||
              isNewUnsupportedTokenPayment ||
              !isPayRemainingMode;

            if (!shouldTriggerSessionSuccess) {
              console.log(
                "[PaymentPage] Session payment detected but waiting for new payment. Current:",
                currentPaidAmount,
                "Initial:",
                initialPaid,
              );
              return;
            }

            clearInterval(statusPollingRef.current);

            // Determine payment type - check transaction level first for unsupported tokens
            let paymentType = "completed";
            const txIsUnsupported =
              txData.isUnsupportedToken === true ||
              txData.is_unsupported_token === true ||
              txData.status === "not_supported_token";

            if (txIsUnsupported || sessionStatus === "paid_unsupported_token") {
              paymentType = "unsupported_token";
            } else if (sessionStatus === "partially_paid") {
              paymentType = "partial";
            } else if (sessionStatus === "overpaid") {
              paymentType = "overpaid";
            }

            setConfirmationStatus(txData.confirmationStatus || "pending");
            setBlocksConfirmed(txData.blocksConfirmed || 0);
            setRequiredConfirmations(txData.requiredConfirmations || 1);

            setPaidDetails({
              txHash:
                sessionData?.txHash || sessionData?.tx_hash || txData.txHash,
              paidAt: sessionData?.paidAt || sessionData?.paid_at,
              paidAmount: currentPaidAmount,
              paymentType: paymentType,
              amountRemaining: parseFloat(
                sessionData?.remainingAmount ||
                  sessionData?.remaining_amount ||
                  0,
              ),
              totalAmount: parseFloat(
                sessionData?.amount || paymentLink?.amount || 0,
              ),
              isFullyPaid:
                sessionStatus === "paid" || sessionStatus === "overpaid",
              transactions: sessionData?.transactions || [],
              transactionId: txData.transactionId,
              confirmationStatus: txData.confirmationStatus || "pending",
              blocksConfirmed: txData.blocksConfirmed || 0,
              requiredConfirmations: txData.requiredConfirmations || 1,
            });

            if (sessionData?.successUrl) {
              setPaymentLink((prev) => ({
                ...prev,
                successUrl: sessionData.successUrl,
              }));
            }

            if (paymentType === "unsupported_token" && txData.transactionId) {
              lastKnownUnsupportedTxRef.current = txData.transactionId;
            }

            setPaymentSuccess(true);

            // Payment sessions are single-use: stop polling after ANY payment
            // No need to update baseline or keep tracking - session is done after first payment
            isPayRemainingFlowRef.current = false;
            console.log(
              `[PaymentSession] Payment received. Session is now complete (single-use).`,
            );
          } else if (
            sessionStatus === "expired" ||
            sessionStatus === "cancelled"
          ) {
            clearInterval(statusPollingRef.current);
            setErrorCode("LINK_EXPIRED");
            setPaymentSession(null);
          }
          return;
        }

        if (isApiKeyPayment && createdPaymentLinkId) {
          if (data.paid) {
            clearInterval(statusPollingRef.current);
            setPaidDetails({
              txHash: data.data?.txHash,
              paidAt: data.data?.paidAt,
              paidAmount: data.data?.paidAmount || paymentSession?.tokenAmount,
            });
            setPaymentSuccess(true);
          } else if (data.expired) {
            clearInterval(statusPollingRef.current);
            setErrorCode("LINK_EXPIRED");
            setPaymentSession(null);
          }
          return;
        }

        const invoiceData = data.data;
        const currentStatus = invoiceData?.status;
        const currentAmountPaid = parseFloat(
          invoiceData?.amount_paid || invoiceData?.amountPaid || 0,
        );
        const initialPaid = parseFloat(initialAmountPaidRef.current || 0);

        const paymentStatuses = [
          "paid",
          "partially_paid",
          "overpaid",
          "paid_unsupported_token",
        ];
        const hasPaymentStatus = paymentStatuses.includes(currentStatus);
        const hasNewPayment = currentAmountPaid > initialPaid;

        const isExpired =
          data.errorCode === "LINK_EXPIRED" ||
          currentStatus === "expired" ||
          currentStatus === "overdue";

        const isPayRemainingMode = isPayRemainingFlowRef.current;

        const latestTx =
          invoiceData?.latestTransaction ||
          invoiceData?.transactions?.[0] ||
          {};

        // Check if there's a new unsupported token payment
        // This can happen either at invoice level (status='paid_unsupported_token')
        // OR at transaction level (individual transaction is unsupported)
        const hasUnsupportedTokenAtInvoiceLevel =
          currentStatus === "paid_unsupported_token";
        const hasUnsupportedTokenAtTxLevel =
          latestTx.isUnsupportedToken === true ||
          latestTx.is_unsupported_token === true ||
          latestTx.status === "not_supported_token";

        const isNewUnsupportedTokenPayment =
          (hasUnsupportedTokenAtInvoiceLevel || hasUnsupportedTokenAtTxLevel) &&
          latestTx.transactionId &&
          latestTx.transactionId !== lastKnownUnsupportedTxRef.current;

        console.log("[PaymentPage] Status check:", {
          currentStatus,
          currentAmountPaid,
          initialPaid,
          hasNewPayment,
          isPayRemainingMode,
          hasPaymentStatus,
          isNewUnsupportedTokenPayment,
          hasUnsupportedTokenAtInvoiceLevel: hasUnsupportedTokenAtInvoiceLevel,
          hasUnsupportedTokenAtTxLevel: hasUnsupportedTokenAtTxLevel,
          latestTxId: latestTx.transactionId,
          latestTxIsUnsupported:
            latestTx.isUnsupportedToken || latestTx.is_unsupported_token,
          lastKnownUnsupportedTxId: lastKnownUnsupportedTxRef.current,
          errorCode: data.errorCode,
          success: data.success,
        });

        // Trigger success if:
        // 1. There's a new payment (amount increased)
        // 2. New unsupported token payment detected
        // 3. Status indicates payment received (for first-time visitors to already-paid links)
        //    BUT NOT if we're in pay-remaining mode (waiting for new payment)
        //    AND NOT if it's the same old unsupported token payment
        const isOldUnsupportedTokenPayment =
          currentStatus === "paid_unsupported_token" &&
          latestTx.transactionId &&
          latestTx.transactionId === lastKnownUnsupportedTxRef.current;

        const shouldTriggerSuccess =
          hasNewPayment ||
          isNewUnsupportedTokenPayment ||
          (!isPayRemainingMode &&
            hasPaymentStatus &&
            !isOldUnsupportedTokenPayment &&
            (data.errorCode === "ALREADY_PAID" || data.success));

        if (shouldTriggerSuccess) {
          clearInterval(statusPollingRef.current);

          const latestTx =
            invoiceData?.latestTransaction ||
            invoiceData?.transactions?.[0] ||
            {};

          // Determine payment type - check transaction level first for unsupported tokens
          let paymentType = "received";
          const txIsUnsupported =
            latestTx.isUnsupportedToken === true ||
            latestTx.is_unsupported_token === true ||
            latestTx.status === "not_supported_token";

          if (txIsUnsupported || currentStatus === "paid_unsupported_token") {
            paymentType = "unsupported_token";
          } else if (currentStatus === "paid") {
            paymentType = "completed";
          } else if (currentStatus === "overpaid") {
            paymentType = "overpaid";
          } else if (currentStatus === "partially_paid") {
            paymentType = "partial";
          }
          setConfirmationStatus(latestTx.confirmationStatus || "pending");
          setBlocksConfirmed(latestTx.blocksConfirmed || 0);
          setRequiredConfirmations(latestTx.requiredConfirmations || 1);

          setPaidDetails({
            txHash:
              invoiceData?.txHash || invoiceData?.tx_hash || latestTx.txHash,
            paidAt:
              invoiceData?.paidAt ||
              invoiceData?.paid_at ||
              new Date().toISOString(),
            paidAmount: currentAmountPaid,
            paymentType: paymentType,
            amountRemaining: parseFloat(
              invoiceData?.amount_remaining ||
                invoiceData?.amountRemaining ||
                0,
            ),
            totalAmount: parseFloat(
              invoiceData?.originalAmount || invoiceData?.amount || 0,
            ),
            transactionId: latestTx.transactionId,
            confirmationStatus: latestTx.confirmationStatus || "pending",
            blocksConfirmed: latestTx.blocksConfirmed || 0,
            requiredConfirmations: latestTx.requiredConfirmations || 1,
          });
          setPaymentSuccess(true);
          initialAmountPaidRef.current = null;
          isPayRemainingFlowRef.current = false;
          if (paymentType === "unsupported_token" && latestTx.transactionId) {
            lastKnownUnsupportedTxRef.current = latestTx.transactionId;
          }
        } else if (isExpired) {
          clearInterval(statusPollingRef.current);
          setErrorCode("LINK_EXPIRED");
          setPaymentLink((prev) => ({
            ...prev,
            status: currentStatus || "expired",
          }));
          setPaymentSession(null);
          initialAmountPaidRef.current = null;
        }
      } catch (err) {
        console.error("Error checking payment status:", err);
      }
    };

    statusPollingRef.current = setInterval(checkPaymentStatus, 2000);
    checkPaymentStatus();

    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, [
    paymentSession,
    isInvoice,
    isSessionPayment,
    sessionId,
    isApiKeyPayment,
    createdPaymentLinkId,
    linkId,
    paymentLink?.amountPaid,
    paymentLink?.amount_paid,
  ]);

  const getRedirectUrl = useCallback(() => {
    if (urlReturnUrl) {
      try {
        new URL(urlReturnUrl);
        return urlReturnUrl;
      } catch {}
    }
    if (paymentLink?.successUrl) {
      try {
        new URL(paymentLink.successUrl);
        return paymentLink.successUrl;
      } catch {}
    }
    if (referrerUrl.current) {
      return referrerUrl.current;
    }
    return null;
  }, [urlReturnUrl, paymentLink?.successUrl]);

  useEffect(() => {
    if (paymentSuccess) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    }
  }, [paymentSuccess]);

  useEffect(() => {
    const canPollViaSession = isSessionPayment && sessionId;
    const canPollViaInvoice = isInvoice && linkId;
    const canPollViaTransaction = paidDetails?.transactionId;

    // Start polling if payment succeeded and not yet confirmed
    // Allow polling via session, invoice, or direct transaction lookup
    if (
      !paymentSuccess ||
      confirmationStatus === "confirmed" ||
      (!canPollViaSession && !canPollViaInvoice && !canPollViaTransaction)
    ) {
      if (confirmationPollingRef.current) {
        clearInterval(confirmationPollingRef.current);
      }
      return;
    }

    const checkConfirmationStatus = async () => {
      try {
        let data;

        if (paidDetails?.transactionId) {
          const response = await fetch(
            `${API_BASE_URL}/transactions/${paidDetails.transactionId}/confirmation-status`,
          );
          data = await response.json();

          if (data.success) {
            setBlocksConfirmed(data.data.blocksConfirmed || 0);
            setRequiredConfirmations(data.data.requiredConfirmations || 1);
            setConfirmationStatus(data.data.confirmationStatus);

            setPaidDetails((prev) => ({
              ...prev,
              confirmationStatus: data.data.confirmationStatus,
              blocksConfirmed: data.data.blocksConfirmed,
              requiredConfirmations: data.data.requiredConfirmations,
            }));

            if (data.data.confirmationStatus === "confirmed") {
              clearInterval(confirmationPollingRef.current);
            }
          }
        } else if (canPollViaSession || canPollViaInvoice) {
          let apiEndpoint;
          if (canPollViaSession) {
            apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/status`;
          } else {
            apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}`;
          }

          const response = await fetch(apiEndpoint);
          data = await response.json();

          if (data.success && data.data) {
            const latestTx =
              data.data.latestTransaction || data.data.transactions?.[0];

            if (latestTx) {
              if (latestTx.transactionId && !paidDetails?.transactionId) {
                setPaidDetails((prev) => ({
                  ...prev,
                  transactionId: latestTx.transactionId,
                }));
              }

              setBlocksConfirmed(latestTx.blocksConfirmed || 0);
              setRequiredConfirmations(latestTx.requiredConfirmations || 1);
              setConfirmationStatus(latestTx.confirmationStatus || "pending");

              setPaidDetails((prev) => ({
                ...prev,
                confirmationStatus: latestTx.confirmationStatus || "pending",
                blocksConfirmed: latestTx.blocksConfirmed || 0,
                requiredConfirmations: latestTx.requiredConfirmations || 1,
                transactionId: latestTx.transactionId || prev.transactionId,
              }));

              if (latestTx.confirmationStatus === "confirmed") {
                clearInterval(confirmationPollingRef.current);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error checking confirmation status:", err);
      }
    };

    confirmationPollingRef.current = setInterval(checkConfirmationStatus, 5000);
    checkConfirmationStatus();

    return () => {
      if (confirmationPollingRef.current) {
        clearInterval(confirmationPollingRef.current);
      }
    };
  }, [
    paymentSuccess,
    confirmationStatus,
    paidDetails?.transactionId,
    isSessionPayment,
    sessionId,
    isInvoice,
    linkId,
  ]);

  useEffect(() => {
    if (confirmationStatus === "confirmed" && paymentSuccess) {
      // const isPartialPayment = paidDetails?.paymentType === 'partial';
      // const isUnsupportedTokenPayment = paidDetails?.paymentType === 'unsupported_token';

      // if (!isPartialPayment && !isUnsupportedTokenPayment) {

      //   let redirectUrl = null;

      //   if (urlReturnUrl) {
      //     try {
      //       new URL(urlReturnUrl);
      //       redirectUrl = urlReturnUrl;
      //     } catch {
      //     }
      //   }

      //   if (!redirectUrl && paymentLink?.successUrl) {
      //     try {
      //       new URL(paymentLink.successUrl);
      //       redirectUrl = paymentLink.successUrl;
      //     } catch {
      //     }
      //   }

      //   if (redirectUrl) {
      //     const redirectTimer = setTimeout(() => {
      //       window.location.href = redirectUrl;
      //     }, 2000);
      //     return () => clearTimeout(redirectTimer);
      //   }
      // }

      let redirectUrl = null;

      if (urlReturnUrl) {
        try {
          new URL(urlReturnUrl);
          redirectUrl = urlReturnUrl;
        } catch {}
      }

      if (!redirectUrl && paymentLink?.successUrl) {
        try {
          new URL(paymentLink.successUrl);
          redirectUrl = paymentLink.successUrl;
        } catch {}
      }

      if (redirectUrl) {
        const redirectTimer = setTimeout(() => {
          window.location.href = redirectUrl;
        }, 2000);
        return () => clearTimeout(redirectTimer);
      }
    }
  }, [
    confirmationStatus,
    paymentSuccess,
    paidDetails?.paymentType,
    urlReturnUrl,
    paymentLink?.successUrl,
  ]);

  // Auto-switch to required chain when wallet connects
  useEffect(() => {
    if (!isConnected || !paymentSession || !paymentSession.network) {
      return;
    }

    // Auto-switch ONCE per connected wallet + session + chain. The flag used to
    // be cleared whenever the wallet reported the right chain, so choosing
    // another network in MetaMask -- or the chain id flickering mid-switch --
    // re-triggered the switch every time and stacked "Switched to ..." toasts.
    // A later network change is the payer's choice; handleWalletConnect
    // switches back when they press Pay Now.
    // Normalize chainId to number for comparison (API may return string)
    const requiredChainId = parseInt(paymentSession.network.chainId);

    const switchKey = `${walletAddress}-${paymentSession.session_id || paymentSession.link_id}-${requiredChainId}`;

    if (!requiredChainId || !currentChainId) return;
    if (autoSwitchAttemptedRef.current === switchKey) return;
    autoSwitchAttemptedRef.current = switchKey;

    if (currentChainId !== requiredChainId) {

      const autoSwitchChain = async () => {
        try {
          console.log("[PaymentPage] Auto-switching to required chain:", {
            requiredChain: requiredChainId,
            currentChain: currentChainId,
            network: paymentSession.network.name,
          });

          await addAndSwitchNetwork({
            chainId: requiredChainId,
            name: paymentSession.network.name,
            rpcUrl: paymentSession.network.rpcUrl,
            nativeCurrency: paymentSession.network.nativeCurrency,
            nativeCurrencyDecimals:
              paymentSession.network.nativeCurrencyDecimals,
            blockExplorerUrl: paymentSession.network.blockExplorerUrl,
          });

          console.log(
            "[PaymentPage] Chain switched automatically to",
            paymentSession.network.name,
          );
          // Fixed id: one toast on screen however often this runs.
          toast.success(`Switched to ${paymentSession.network.name}`, { id: "chain-switch" });
        } catch (error) {
          // Not retried automatically: re-prompting every time the chain id
          // changes is what produced the toast storm. Pay Now switches again.
          console.warn(
            "[PaymentPage] Auto-switch failed (user may have rejected; Pay Now will retry):",
            error.message,
          );
        }
      };

      autoSwitchChain();
    }
  }, [isConnected, walletAddress, paymentSession, currentChainId]);

  const formatTimeRemaining = (ms) => {
    if (!ms || ms <= 0) return "00:00";
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleProceedToPay = async () => {
    if (!selectedNetwork || !selectedToken) return;

    setIsInitiatingPayment(true);
    try {
      let apiEndpoint;
      let requestBody = {
        networkId: parseInt(selectedNetwork),
        tokenId: parseInt(selectedToken),
      };

      if (isSessionPayment) {
        apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/initiate`;
      } else if (isInvoice) {
        apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}/initiate-payment`;
      } else if (isApiKeyPayment) {
        apiEndpoint = `${API_BASE_URL}/api-keys/public/${linkId}/initiate-payment`;
        if (paymentLink?.isDynamicAmount && paymentLink?.amount) {
          requestBody.amount = paymentLink.amount;
          requestBody.currency = paymentLink.currency;
          if (urlSignature) {
            requestBody.signature = urlSignature;
          }
        }
      } else {
        apiEndpoint = `${API_BASE_URL}/payment-links/public/${linkId}/initiate-payment`;
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success) {
        sessionInitiatedRef.current = true;

        const amountAlreadyPaid = parseFloat(
          data.data?.amountPaid ||
            data.data?.amount_paid ||
            data.data?.totalPaidUsd ||
            paymentLink?.amountPaid ||
            paymentLink?.amount_paid ||
            paymentLink?.totalPaidUsd ||
            0,
        );
        initialAmountPaidRef.current = amountAlreadyPaid;
        console.log(
          "[PaymentPage] Session started. API response amountPaid:",
          data.data?.amountPaid,
          "paymentLink amountPaid:",
          paymentLink?.amountPaid,
        );
        console.log(
          "[PaymentPage] Initial amount paid set to:",
          initialAmountPaidRef.current,
          "isPayRemainingMode:",
          isPayRemainingFlowRef.current,
        );

        setPaymentSession(data.data);

        const SESSION_DURATION_MS = 15 * 60 * 1000;
        if (data.data.sessionExpiresAt) {
          setSessionExpiresAt(data.data.sessionExpiresAt);
        } else if (data.data.timeRemainingMs) {
          setSessionExpiresAt(
            new Date(Date.now() + data.data.timeRemainingMs).toISOString(),
          );
        } else if (data.data.expiresAt) {
          setSessionExpiresAt(data.data.expiresAt);
        } else {
          setSessionExpiresAt(
            new Date(Date.now() + SESSION_DURATION_MS).toISOString(),
          );
        }

        if (isApiKeyPayment && data.data.paymentLinkId) {
          setCreatedPaymentLinkId(data.data.paymentLinkId);
        }

        const eip681Uri = generateEIP681Uri(
          data.data.receivingAddress,
          data.data.tokenAmount,
          data.data.network,
          data.data.token,
        );
        if (eip681Uri) {
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(eip681Uri)}`;
          setQrCodeUrl(qrUrl);
        }
      } else {
        // Handle SESSION_ALREADY_PAID error for payment sessions
        if (data.errorCode === "SESSION_ALREADY_PAID") {
          setError(
            "Payment session has already received payment and cannot be reused.",
          );
          setErrorCode("SESSION_ALREADY_PAID");
        } else {
          setError(data.message || "Failed to initiate payment");
          setErrorCode(data.errorCode);
        }
      }
    } catch (err) {
      console.error("Failed to initiate payment:", err);
      setError("Failed to initiate payment. Please try again.");
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  const handleChangeSelection = async () => {
    setIsReleasingSession(true);
    try {
      let apiEndpoint;
      let requestBody = {};

      if (isSessionPayment) {
        apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/release-session`;

        // Pass walletAddress and networkId for payment sessions too
        if (paymentSession?.receivingAddress) {
          requestBody = {
            walletAddress: paymentSession.receivingAddress,
            networkId: paymentSession.network?.id || null,
          };
        }
      } else if (isInvoice) {
        apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}/release-session`;

        if (paymentSession?.receivingAddress) {
          requestBody = {
            walletAddress: paymentSession.receivingAddress,
            networkId: paymentSession.network?.id || null,
          };
        }
      } else if (isApiKeyPayment) {
        apiEndpoint = `${API_BASE_URL}/api-keys/public/${linkId}/release-session`;
        requestBody = { paymentLinkId: createdPaymentLinkId };
      } else {
        apiEndpoint = `${API_BASE_URL}/payment-links/public/${linkId}/release-session`;
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (data.success) {
        setPaymentSession(null);
        setSessionExpiresAt(null);
        setSessionTimeRemaining(null);
        setQrCodeUrl("");
        setSelectedNetwork("");
        setSelectedToken("");
        setTokens([]);
        setCreatedPaymentLinkId(null);
      } else {
        console.error("Failed to release session:", data.message);
      }
    } catch (err) {
      console.error("Failed to release session:", err);
    } finally {
      setIsReleasingSession(false);
    }
  };

  const handleCopyAddress = () => {
    if (paymentSession?.receivingAddress) {
      navigator.clipboard.writeText(paymentSession.receivingAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWalletConnect = async () => {
    if (!paymentSession) return;

    const { network, token, receivingAddress, tokenAmount } = paymentSession;

    if (!isConnected) {
      openWalletModal();
      return;
    }

    setIsWalletProcessing(true);

    try {
      // Normalize chainId to number for comparison (API may return string)
      const requiredChainId = parseInt(network?.chainId);
      if (network?.chainId && currentChainId !== requiredChainId) {
        try {
          console.log("[PaymentPage] Manual network switch requested:", {
            requiredChainId,
            currentChainId,
            networkName: network.name,
          });
          // Use web3Utils to add chain if needed and switch to it
          await addAndSwitchNetwork({
            chainId: requiredChainId,
            name: network.name,
            rpcUrl: network.rpcUrl,
            nativeCurrency: network.nativeCurrency,
            nativeCurrencyDecimals: network.nativeCurrencyDecimals,
            blockExplorerUrl: network.blockExplorerUrl,
          });
          toast.success(`Switched to ${network.name}`, { id: "chain-switch" });
        } catch (switchError) {
          console.error("Failed to add/switch network:", switchError);

          if (switchError.message?.includes("User rejected")) {
            toast.error("Network switch rejected by user.");
          } else if (switchError.message?.includes("MetaMask not detected")) {
            toast.error(
              "MetaMask not detected. Please install MetaMask extension.",
            );
          } else {
            toast.error(
              `Failed to switch to ${network.name}. ${switchError.message || "Please try again."}`,
            );
          }

          setIsWalletProcessing(false);
          return;
        }
      }

      const tokenDecimals = token?.decimals || 18;
      let txHash;

      if (!token?.contractAddress || token?.isNative) {
        toast.loading("Please sign the transaction in your wallet...", {
          id: "sign-tx",
        });

        const value = parseUnits(tokenAmount.toString(), tokenDecimals);

        txHash = await sendTransactionAsync({
          to: receivingAddress,
          value: value,
        });

        toast.dismiss("sign-tx");
      } else {
        toast.loading("Please sign the transaction in your wallet...", {
          id: "sign-tx",
        });

        const erc20Abi = [
          {
            name: "transfer",
            type: "function",
            inputs: [
              { name: "to", type: "address" },
              { name: "amount", type: "uint256" },
            ],
            outputs: [{ name: "", type: "bool" }],
          },
        ];

        const amount = parseUnits(tokenAmount.toString(), tokenDecimals);

        txHash = await writeContractAsync({
          address: token.contractAddress,
          abi: erc20Abi,
          functionName: "transfer",
          args: [receivingAddress, amount],
        });

        toast.dismiss("sign-tx");
      }

      setPendingTxHash(txHash);
      toast.success(
        `Transaction sent! Hash: ${txHash.slice(0, 10)}...${txHash.slice(-8)}`,
      );
    } catch (err) {
      console.error("Wallet connect error:", err);
      toast.dismiss("sign-tx");

      if (
        err.message?.includes("user rejected") ||
        err.message?.includes("User rejected") ||
        err.code === 4001
      ) {
        toast.error("Transaction rejected by user.");
      } else if (err.message?.includes("insufficient funds")) {
        toast.error("Insufficient funds in wallet.");
      } else {
        toast.error("Failed to send transaction. Please try again.");
      }

      setIsWalletProcessing(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!paymentSession) return;

    setIsVerifying(true);
    setVerifyMessage("");

    try {
      let apiEndpoint;
      let useGetMethod = false;

      if (isSessionPayment) {
        apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/status`;
        useGetMethod = true;
      } else if (isInvoice) {
        apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}/verify-payment`;
      } else if (isApiKeyPayment && createdPaymentLinkId) {
        apiEndpoint = `${API_BASE_URL}/api-keys/public/verify/${createdPaymentLinkId}`;
        useGetMethod = true;
      } else {
        apiEndpoint = `${API_BASE_URL}/payment-links/public/${linkId}/verify-payment`;
      }

      const response = await fetch(apiEndpoint, {
        method: useGetMethod ? "GET" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (isSessionPayment) {
        const sessionStatus = data.data?.status;
        const sessionData = data.data;
        if (
          [
            "paid",
            "partially_paid",
            "overpaid",
            "paid_unsupported_token",
          ].includes(sessionStatus)
        ) {
          let paymentType = "completed";
          if (sessionStatus === "partially_paid") paymentType = "partial";
          else if (sessionStatus === "overpaid") paymentType = "overpaid";
          else if (sessionStatus === "paid_unsupported_token")
            paymentType = "unsupported_token";

          const txData =
            sessionData?.latestTransaction ||
            sessionData?.transactions?.[0] ||
            {};
          setConfirmationStatus(txData.confirmationStatus || "pending");
          setBlocksConfirmed(txData.blocksConfirmed || 0);
          setRequiredConfirmations(txData.requiredConfirmations || 1);

          setPaidDetails({
            txHash:
              sessionData?.txHash || sessionData?.tx_hash || txData.txHash,
            paidAt: sessionData?.paidAt || sessionData?.paid_at,
            paidAmount: parseFloat(
              sessionData?.totalPaidUsd ||
                sessionData?.total_paid_usd ||
                sessionData?.paidAmount ||
                0,
            ),
            paymentType: paymentType,
            amountRemaining: parseFloat(
              sessionData?.remainingAmount ||
                sessionData?.remaining_amount ||
                0,
            ),
            totalAmount: parseFloat(
              sessionData?.amount || paymentLink?.amount || 0,
            ),
            isFullyPaid:
              sessionStatus === "paid" || sessionStatus === "overpaid",
            transactions: sessionData?.transactions || [],
            transactionId: txData.transactionId,
            confirmationStatus: txData.confirmationStatus || "pending",
            blocksConfirmed: txData.blocksConfirmed || 0,
            requiredConfirmations: txData.requiredConfirmations || 1,
          });
          setPaymentSuccess(true);
        } else if (
          sessionStatus === "expired" ||
          sessionStatus === "cancelled"
        ) {
          setErrorCode("LINK_EXPIRED");
          setPaymentSession(null);
        } else {
          setVerifyMessage(
            "No payment detected yet. Please wait a few moments after sending.",
          );
          setTimeout(() => setVerifyMessage(""), 5000);
        }
      } else if (data.success && data.paid) {
        const invoiceData = data.data;
        const currentStatus = invoiceData?.status;
        let paymentType = "completed";
        if (currentStatus === "paid") paymentType = "completed";
        else if (currentStatus === "overpaid") paymentType = "overpaid";
        else if (currentStatus === "partially_paid") paymentType = "partial";
        else if (currentStatus === "paid_unsupported_token")
          paymentType = "unsupported_token";

        const latestTx =
          invoiceData?.latestTransaction ||
          invoiceData?.transactions?.[0] ||
          {};
        setConfirmationStatus(latestTx.confirmationStatus || "pending");
        setBlocksConfirmed(latestTx.blocksConfirmed || 0);
        setRequiredConfirmations(latestTx.requiredConfirmations || 1);

        setPaidDetails({
          txHash:
            invoiceData?.txHash || invoiceData?.tx_hash || latestTx.txHash,
          paidAt:
            invoiceData?.paidAt ||
            invoiceData?.paid_at ||
            new Date().toISOString(),
          paidAmount: parseFloat(
            invoiceData?.amount_paid || invoiceData?.amountPaid || 0,
          ),
          paymentType: paymentType,
          amountRemaining: parseFloat(
            invoiceData?.amount_remaining || invoiceData?.amountRemaining || 0,
          ),
          totalAmount: parseFloat(
            invoiceData?.originalAmount || invoiceData?.amount || 0,
          ),
          transactionId: latestTx.transactionId,
          confirmationStatus: latestTx.confirmationStatus || "pending",
          blocksConfirmed: latestTx.blocksConfirmed || 0,
          requiredConfirmations: latestTx.requiredConfirmations || 1,
        });
        setPaymentSuccess(true);
      } else if (data.expired) {
        setErrorCode("LINK_EXPIRED");
        setPaymentLink((prev) => ({ ...prev, status: "overdue" }));
        setPaymentSession(null);
      } else {
        setVerifyMessage(
          data.message ||
            "No payment detected yet. Please wait a few moments after sending.",
        );
        setTimeout(() => setVerifyMessage(""), 5000);
      }
    } catch (err) {
      console.error("Error verifying payment:", err);
      setVerifyMessage("Failed to verify payment. Please try again.");
      setTimeout(() => setVerifyMessage(""), 5000);
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-loading">
            <div className="loading-spinner" />
            <p>Loading payment details...</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error && !paymentLink) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card error">
            <AlertCircle size={48} />
            <h2>Payment Link Error</h2>
            <p>{error}</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // The amount due on THIS payment, and the partial-payment context around it.
  //
  // Every backend that can be partially paid already returns the authoritative
  // figure; the shapes just differ. The invoice API returns amountToPay /
  // amountRemaining / isPartialPayment, payment sessions return amountToPay /
  // isPartiallyPaid, and plain payment links have no partial concept at all.
  // Reading only paymentLink.isPartiallyPaid / paymentLink.remainingAmount (the
  // session shape) silently fell through to paymentLink.amount for invoices,
  // which is the ORIGINAL total on the paths where the API does not pre-substitute
  // it — that is how a $12 invoice with $4 outstanding showed "$12 = 4 USDT".
  //
  // Once a session exists the backend has already priced it, so its amountToPay
  // is preferred over anything inferred from the link payload.
  // Number(null) and Number('') are both 0 — which is finite — so absent values
  // must be rejected before the cast, or a null placeholder in the chain below
  // would win as a legitimate 0.
  const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const firstNum = (...vals) => {
    for (const v of vals) {
      const n = num(v);
      if (n !== null) return n;
    }
    return null;
  };

  const amountPaidSoFar =
    firstNum(
      paymentLink?.amountPaid,
      paymentLink?.amount_paid,
      paymentLink?.totalPaidUsd,
    ) || 0;

  // Only used to decide which remaining-amount field to trust below — this does
  // NOT drive any visual state.
  const isPartiallyPaidNow = Boolean(
    paymentSession?.isPartiallyPaid ||
    paymentSession?.isPartialPayment ||
    paymentLink?.isPartiallyPaid ||
    paymentLink?.isPartialPayment ||
    paymentLink?.status === "partially_paid" ||
    amountPaidSoFar > 0,
  );

  // What the customer must send now.
  const amountDueNow =
    firstNum(
      paymentSession?.amountToPay,
      paymentSession?.amountRemaining,
      isPartiallyPaidNow
        ? firstNum(
            paymentLink?.remainingAmount,
            paymentLink?.amountRemaining,
            paymentLink?.amount_remaining,
          )
        : null,
      paymentLink?.amount,
    ) || 0;

  if (paymentSuccess && paidDetails) {
    const isPartialPayment = paidDetails?.paymentType === "partial";
    const isOverpaid = paidDetails?.paymentType === "overpaid";
    const isUnsupportedToken = paidDetails?.paymentType === "unsupported_token";
    const amountRemaining = paidDetails?.amountRemaining || 0;
    const isConfirmed = confirmationStatus === "confirmed";
    const confirmProgress =
      requiredConfirmations > 0
        ? Math.min(100, (blocksConfirmed / requiredConfirmations) * 100)
        : 0;

    /* Headline, stated honestly for every combination of state.
     *
     * The old expression fell through to 'Payment Successful!' for ANY
     * unconfirmed payment, so a partial payment still awaiting its first
     * confirmation announced success while the same card showed
     * "Partial Payment" and the full amount still outstanding. An unsupported
     * token did the same, and there the original amount is genuinely still due.
     *
     * 'Payment confirmed' is now reserved for a payment that is both full and
     * verified on chain. Everything else says what actually happened. */
    const headline = isUnsupportedToken
      ? "Unsupported token received"
      : isPartialPayment
        ? "Partial payment received"
        : isOverpaid
          ? "Payment received"
          : isConfirmed
            ? "Payment confirmed"
            : "Payment received";

    /* Colour follows the same truth. Green was previously unconditional, so a
       short or unsupported payment was presented in the success colour. */
    const headlineTone = isUnsupportedToken
      ? "warn"
      : isPartialPayment
        ? "partial"
        : isConfirmed
          ? "ok"
          : "pending";

    // A confirmed payment fills the ring; otherwise it tracks real progress.
    const ringProgress = isConfirmed ? 100 : confirmProgress;

    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-success-overlay">
            {/*
              Ring shows real confirmation progress, replacing a 24px spinner
              that was absolutely positioned at the icon's bottom-right corner
              and read as a rendering glitch rather than an indicator.
            */}
            <div className="success-animation">
              <div className={`pay-mark pay-mark--${headlineTone}`}>
                <svg
                  className="pay-mark__ring"
                  viewBox="0 0 120 120"
                  aria-hidden="true"
                >
                  <circle className="pay-mark__track" cx="60" cy="60" r="54" />
                  <circle
                    className="pay-mark__fill"
                    cx="60"
                    cy="60"
                    r="54"
                    strokeDasharray={PAY_RING_C}
                    /* Offset is the UNFILLED remainder, so 0% is a full offset
                       and 100% is none. */
                    strokeDashoffset={PAY_RING_C * (1 - ringProgress / 100)}
                  />
                </svg>
                <span className="pay-mark__glyph">
                  {isConfirmed ? (
                    <CheckCircle size={44} />
                  ) : (
                    <Shield size={40} />
                  )}
                </span>
              </div>
            </div>
            <h2 className={`pay-headline pay-headline--${headlineTone}`}>
              {headline}
            </h2>
            <p className="success-amount">
              ${formatUSD(paidDetails?.paidAmount || paymentLink?.amount || 0)}{" "}
              {paymentLink?.currency || "USD"}
            </p>
            {paymentSession?.token?.symbol && (
              <p className="success-token-amount">
                {formatCrypto(paidDetails?.paidAmount || 0)}{" "}
                {paymentSession.token.symbol}
              </p>
            )}

            {requiredConfirmations > 0 && (
              <div
                className={`confirmation-card ${isConfirmed ? "confirmed" : "pending"}`}
              >
                <div className="confirmation-header">
                  <div className="confirmation-icon">
                    {isConfirmed ? (
                      <CheckCircle size={20} />
                    ) : (
                      <Loader2 size={20} className="spinning" />
                    )}
                  </div>
                  <div className="confirmation-title">
                    {isConfirmed
                      ? "Transaction Confirmed"
                      : "Verifying on Blockchain"}
                  </div>
                </div>

                {!isConfirmed && (
                  <>
                    <div className="confirmation-progress-bar">
                      <div
                        className="confirmation-progress-fill"
                        style={{ width: `${confirmProgress}%` }}
                      />
                    </div>
                    <div className="confirmation-counts">
                      <span>
                        {blocksConfirmed} of {requiredConfirmations}{" "}
                        confirmations
                      </span>
                      {/* Rounded for display only; the bar width uses the
                          unrounded value. */}
                      <span className="confirmation-pct">
                        {Math.round(confirmProgress)}%
                      </span>
                    </div>
                    <p className="confirmation-message">
                      We are checking your transaction on the blockchain. This
                      usually takes a few moments, and you do not need to do
                      anything else.
                    </p>
                  </>
                )}

                {isConfirmed && (
                  <p className="confirmation-message success">
                    Your transaction has been fully verified with{" "}
                    {blocksConfirmed} block confirmations.
                  </p>
                )}
              </div>
            )}
            <div
              className={`payment-status-badge ${paidDetails?.paymentType || "completed"}`}
            >
              {isUnsupportedToken && "Unsupported Token"}
              {isPartialPayment && !isUnsupportedToken && "Partial Payment"}
              {isOverpaid && !isUnsupportedToken && "Overpaid"}
              {!isPartialPayment &&
                !isOverpaid &&
                !isUnsupportedToken &&
                "Fully Paid"}
            </div>
            {isUnsupportedToken && (
              <div className="unsupported-token-warning">
                <AlertCircle size={20} />
                <div>
                  <strong>This token is not supported</strong>
                  <p>
                    The payment was received but the token is not supported.
                    Please contact support or pay again with a supported token.
                    The original amount is still due.
                  </p>
                </div>
              </div>
            )}
            {isPartialPayment && amountRemaining > 0 && (
              <div className="remaining-info">
                <span>Amount Remaining:</span>
                <strong>${formatUSD(amountRemaining)}</strong>
              </div>
            )}
            {isOverpaid && paidDetails?.totalAmount && (
              <div className="overpaid-info">
                <span>Overpaid by:</span>
                <strong>
                  $
                  {formatUSD(
                    parseFloat(paidDetails.paidAmount) -
                      parseFloat(paidDetails.totalAmount),
                  )}
                </strong>
              </div>
            )}

            {(paidDetails?.transactions?.length > 0 || paidDetails?.txHash) && (
              <div className="tx-hash-box">
                <span className="tx-label">Transaction Hash:</span>
                <p className="tx-hash">
                  {(
                    paidDetails.transactions?.[
                      paidDetails.transactions.length - 1
                    ]?.txHash || paidDetails.txHash
                  )?.slice(0, 16)}
                  ...
                  {(
                    paidDetails.transactions?.[
                      paidDetails.transactions.length - 1
                    ]?.txHash || paidDetails.txHash
                  )?.slice(-12)}
                </p>
                <button
                  className="copy-tx-btn"
                  onClick={() => {
                    const txHash =
                      paidDetails.transactions?.[
                        paidDetails.transactions.length - 1
                      ]?.txHash || paidDetails.txHash;
                    navigator.clipboard.writeText(txHash);
                    toast.success("Transaction hash copied!");
                  }}
                >
                  <Copy size={16} />
                </button>
              </div>
            )}

            <div
              className={`success-message ${isConfirmed ? "" : "verifying"}`}
            >
              {isConfirmed ? (
                <>
                  <CheckCircle size={18} />
                  <span>Your payment has been received and confirmed</span>
                </>
              ) : (
                <>
                  <Loader2 size={18} className="spinning" />
                  <span>Your payment is being verified on the blockchain</span>
                </>
              )}
            </div>

            <p className="success-note">
              {isPartialPayment
                ? isConfirmed
                  ? isSessionPayment
                    ? "Payment session completed. This was a partial payment. Please contact the merchant for the remaining amount."
                    : "You can make another payment to complete the full amount."
                  : "Please wait while we verify your transaction."
                : isUnsupportedToken
                  ? isConfirmed
                    ? isSessionPayment
                      ? "Payment session completed. The token you sent is not supported. Please contact the merchant."
                      : "The token you sent is not supported. Please try again with a supported token."
                    : "Please wait while we verify your transaction."
                  : isConfirmed
                    ? urlReturnUrl || paymentLink?.successUrl
                      ? "Redirecting you back..."
                      : "You can now close this page."
                    : "Please wait while we verify your transaction."}
            </p>

            {((isPartialPayment && amountRemaining > 0) ||
              isUnsupportedToken) &&
              isConfirmed &&
              !isSessionPayment && (
                <button
                  className="pay-remaining-btn"
                  onClick={async () => {
                    // Clear all polling intervals before resetting state
                    if (statusPollingRef.current) {
                      clearInterval(statusPollingRef.current);
                      statusPollingRef.current = null;
                    }
                    if (confirmationPollingRef.current) {
                      clearInterval(confirmationPollingRef.current);
                      confirmationPollingRef.current = null;
                    }
                    if (timerRef.current) {
                      clearInterval(timerRef.current);
                      timerRef.current = null;
                    }

                    try {
                      let apiEndpoint;
                      let requestBody = {};

                      if (isSessionPayment) {
                        apiEndpoint = `${API_BASE_URL}/v1/payments/sessions/${sessionId}/release-session`;

                        // Pass walletAddress and networkId for payment sessions too
                        if (paymentSession?.receivingAddress) {
                          requestBody = {
                            walletAddress: paymentSession.receivingAddress,
                            networkId: paymentSession.network?.id || null,
                          };
                        }
                      } else if (isInvoice) {
                        apiEndpoint = `${API_BASE_URL}/invoices/public/${linkId}/release-session`;
                        if (paymentSession?.receivingAddress) {
                          requestBody = {
                            walletAddress: paymentSession.receivingAddress,
                            networkId: paymentSession.network?.id || null,
                          };
                        }
                      } else if (isApiKeyPayment) {
                        apiEndpoint = `${API_BASE_URL}/api-keys/public/${linkId}/release-session`;
                        requestBody = { paymentLinkId: createdPaymentLinkId };
                      } else {
                        apiEndpoint = `${API_BASE_URL}/payment-links/public/${linkId}/release-session`;
                      }

                      const releaseResponse = await fetch(apiEndpoint, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(requestBody),
                      });
                      const releaseData = await releaseResponse.json();
                      console.log(
                        "[PayRemaining/Retry] Release response:",
                        releaseData,
                      );
                    } catch (err) {
                      console.error("Failed to release session:", err);
                    }

                    // Reset confirmation status
                    setConfirmationStatus(null);
                    setBlocksConfirmed(0);
                    setRequiredConfirmations(0);

                    // Different behavior for unsupported token vs partial payment
                    if (isUnsupportedToken) {
                      // For unsupported token: User wants to pay with supported token
                      console.log(
                        "[UnsupportedToken] Retrying with supported token",
                      );
                      const currentPaidAmount = paidDetails?.paidAmount || 0;
                      setPaymentSuccess(false);
                      setPaidDetails(null);
                      setPaymentSession(null);
                      setSessionExpiresAt(null);
                      setSessionTimeRemaining(null);
                      setQrCodeUrl("");
                      setSelectedNetwork("");
                      setSelectedToken("");
                      setTokens([]);
                      setCreatedPaymentLinkId(null);
                      sessionInitiatedRef.current = false;

                      // For payment sessions: Keep tracking the amount (even though it was unsupported)
                      // For invoices: Start fresh
                      if (isSessionPayment) {
                        isPayRemainingFlowRef.current = true; // Payment session needs to track for retry
                        initialAmountPaidRef.current = currentPaidAmount; // Track what was paid (unsupported)
                        console.log(
                          "[UnsupportedToken] Payment session - keeping pay-remaining flow active, amount:",
                          currentPaidAmount,
                        );
                      } else {
                        isPayRemainingFlowRef.current = false; // Invoice starts fresh
                        initialAmountPaidRef.current = 0;
                        console.log(
                          "[UnsupportedToken] Invoice - resetting to fresh state",
                        );
                      }
                      // Keep lastKnownUnsupportedTxRef so we don't re-detect the same unsupported token transaction
                    } else {
                      // For partial payment: Track the amount already paid
                      const currentPaidAmount = paidDetails?.paidAmount || 0;
                      console.log(
                        "[PayRemaining] Starting pay remaining flow. Initial paid:",
                        currentPaidAmount,
                      );
                      setPaymentSuccess(false);
                      setPaidDetails(null);
                      setPaymentSession(null);
                      setSessionExpiresAt(null);
                      setSessionTimeRemaining(null);
                      setQrCodeUrl("");
                      setSelectedNetwork("");
                      setSelectedToken("");
                      setTokens([]);
                      setCreatedPaymentLinkId(null);
                      sessionInitiatedRef.current = false;
                      isPayRemainingFlowRef.current = true; // This IS a pay-remaining flow
                      initialAmountPaidRef.current = currentPaidAmount;
                    }

                    await fetchPaymentLink();
                  }}
                >
                  {isUnsupportedToken
                    ? "Pay with Supported Token"
                    : `Pay Remaining $${formatUSD(amountRemaining)}`}
                </button>
              )}
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (errorCode === "SESSION_ALREADY_PAID") {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card expired">
            <AlertCircle size={48} />
            <h2>Session Complete</h2>
            <p>
              This payment session has already received payment and cannot be
              reused.
            </p>
            <p className="status-hint">
              Payment sessions are single-use. Please contact the merchant if
              you need to make an additional payment.
            </p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (errorCode === "ALREADY_PAID" || paymentLink?.status === "paid") {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card success">
            <CheckCircle size={48} />
            <h2>Payment Complete</h2>
            <p>
              This {isInvoice ? "invoice" : "payment link"} has already been
              paid.
            </p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (
    errorCode === "LINK_EXPIRED" ||
    paymentLink?.status === "expired" ||
    paymentLink?.status === "overdue"
  ) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card expired">
            <XCircle size={48} />
            <h2>{isInvoice ? "Invoice Expired" : "Link Expired"}</h2>
            <p>
              This{" "}
              {isInvoice
                ? "invoice has passed its due date"
                : "payment link has expired"}
              .
            </p>
            <p className="status-hint">
              Please request a new {isInvoice ? "invoice" : "payment link"} from
              the merchant.
            </p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (
    errorCode === "LINK_INACTIVE" ||
    paymentLink?.status === "inactive" ||
    paymentLink?.status === "cancelled"
  ) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card inactive">
            <XCircle size={48} />
            <h2>Link Inactive</h2>
            <p>This payment link is no longer active.</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }
  if (errorCode === "NO_API_KEY" || errorCode === "API_KEY_INVALID") {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-status-card error">
            <AlertCircle size={48} />
            <h2>Payment Unavailable</h2>
            <p>
              {error ||
                "This payment link cannot accept payments at this time."}
            </p>
            <p className="status-hint">
              Please contact the merchant for assistance.
            </p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  /* The header now carries MaxelPay branding rather than the merchant's, so the
   * merchant is named beneath the amount instead. A payer still has to be able
   * to see who they are paying; dropping the name entirely would remove that.
   * merchantLogo is no longer read anywhere, so it is gone rather than left as
   * an unused binding. */
  const merchantName = paymentLink?.merchant?.businessName || "";

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className={`payment-card ${paymentSession ? "expanded" : ""}`}>
          <div className="payment-header">
            {/* Decorative only, hence aria-hidden. CSS gradients rather than an
                image, so it scales with the header and costs no request. */}
            <span className="pc-wave" aria-hidden="true" />
            <span className="pc-orb" aria-hidden="true" />

            <div className="pc-brand">
              {/* The full lockup (mark + wordmark) rather than a mark beside
                  the word "MaxelPay" set in Poppins. The artwork carries the
                  real letterforms and spacing; typing the name in the UI font
                  only approximated them.
                  maxelpay-logo-light.svg is the white-on-dark variant, which is
                  what this violet header needs. */}
              <img className="pc-brand__logo" src={imglogo} alt="MaxelPay" />
              {/* <p className="pc-billed-by">
                Billed by <strong>{merchantName}</strong>
              </p> */}
              {merchantName && (
                <p className="pc-billed-by">
                  Billed by <strong>{merchantName}</strong>
                </p>
              )}
              {/* <span className="pc-brand__tag">Crypto Payment Gateway</span> */}
            </div>
          </div>
          <div className="payment-space">
            <div className="payment-amount-section">
              {paymentLink.isPartiallyPaid && (
                <div className="partial-payment-banner">
                  <span className="partial-badge">Partial Payment Made</span>
                  <div className="partial-progress">
                    <span>
                      Paid: ${formatUSD(paymentLink.totalPaidUsd || 0)}
                    </span>
                    <span>of ${formatUSD(paymentLink.amount || 0)}</span>
                  </div>
                </div>
              )}
              <h1 className="payment-amount">${formatUSD(amountDueNow)}</h1>
              {paymentLink.isPartiallyPaid && (
                <p className="remaining-label">Remaining to Pay</p>
              )}
              {paymentSession && paymentSession.tokenAmount && (
                <p className="payment-token-amount">
                  ≈ {formatCrypto(paymentSession.tokenAmount)}{" "}
                  {paymentSession.token?.symbol}
                </p>
              )}
              {/* {merchantName && (
              <p className="pc-billed-by">
                Billed by <strong>{merchantName}</strong>
              </p>
            )} */}
              {paymentLink.name && (
                <p className="payment-name">{paymentLink.name}</p>
              )}
            </div>
            {/* Check if payment session has already received payment */}
            {!paymentSession &&
            sessionTimeRemaining !== 0 &&
            isSessionPayment &&
            (paymentLink?.status === "paid" ||
              paymentLink?.status === "partially_paid" ||
              paymentLink?.status === "overpaid" ||
              paymentLink?.status === "paid_unsupported_token") ? (
              <div className="payment-status-card expired">
                <AlertCircle size={48} />
                <h2>Session Complete</h2>
                <p>
                  This payment session has already received payment and is now
                  closed.
                </p>
                <p className="status-hint">
                  Payment sessions are single-use only. Please contact the
                  merchant if you need to make an additional payment.
                </p>
              </div>
            ) : (
              !paymentSession &&
              sessionTimeRemaining !== 0 && (
                <>
                  <div className="payment-form">
                    <div className="form-group">
                      <label>Network</label>
                      <AssetSelect
                        kind="chain"
                        value={selectedNetwork}
                        onChange={(v) => {
                          setSelectedNetwork(String(v));
                          setSelectedToken("");
                        }}
                        disabled={isLoadingNetworks}
                        placeholder={
                          isLoadingNetworks ? "Loading…" : "Select Network"
                        }
                        options={networks
                          .filter((network) => {
                            const paymentLinkIsTestnet = paymentLink?.isTestnet;
                            const networkIsTestnet = Boolean(network.isTestnet);
                            if (
                              paymentLinkIsTestnet === true ||
                              paymentLinkIsTestnet === 1
                            ) {
                              return networkIsTestnet === true;
                            } else if (
                              paymentLinkIsTestnet === false ||
                              paymentLinkIsTestnet === 0
                            ) {
                              return networkIsTestnet === false;
                            }
                            return true;
                          })
                          .map((network) => ({
                            value: String(network.id),
                            label: network.name,
                            chain: network.slug || network.name,
                            logoUrl: network.logoUrl,
                          }))}
                      />
                    </div>

                    <div className="form-group">
                      <label>Token</label>
                      <AssetSelect
                        kind="token"
                        value={selectedToken}
                        onChange={(v) => setSelectedToken(String(v))}
                        disabled={!selectedNetwork || isLoadingTokens}
                        placeholder={
                          isLoadingTokens
                            ? "Loading…"
                            : !selectedNetwork
                              ? "Select network first"
                              : "Select Token"
                        }
                        options={tokens.map((token) => ({
                          value: String(token.id),
                          label: token.symbol,
                          sublabel: token.name,
                          symbol: token.symbol,
                          logoUrl: token.logoUrl,
                        }))}
                      />
                    </div>
                  </div>

                  <button
                    className="pay-now-btn"
                    onClick={handleProceedToPay}
                    disabled={
                      !selectedNetwork || !selectedToken || isInitiatingPayment
                    }
                  >
                    {isInitiatingPayment ? (
                      <>
                        <RefreshCw size={18} className="spinning" />
                        Preparing Payment...
                      </>
                    ) : (
                      "Proceed to Pay"
                    )}
                  </button>
                </>
              )
            )}

            {paymentSession && (
              <div className="payment-options">
                {sessionTimeRemaining > 0 && (
                  <div
                    className={`session-timer-banner ${sessionTimeRemaining < 60000 ? "critical" : sessionTimeRemaining < 300000 ? "warning" : ""}`}
                  >
                    <div className="timer-icon">
                      <Clock size={18} />
                    </div>
                    <div className="timer-content">
                      <div className="timer-countdown">
                        <span className="timer-value">
                          {formatTimeRemaining(sessionTimeRemaining)}
                        </span>
                        <span className="timer-label">remaining</span>
                      </div>
                      <p className="timer-message">
                        {sessionTimeRemaining < 60000
                          ? "Hurry! Session expiring soon"
                          : sessionTimeRemaining < 300000
                            ? "Complete payment before session expires"
                            : "Session valid for 15 minutes. Complete payment within this time."}
                      </p>
                    </div>
                  </div>
                )}

                <div className="selected-info">
                  <div className="info-item">
                    <span className="info-label">Network:</span>
                    <span className="info-value">
                      {paymentSession.network?.name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Token:</span>
                    <span className="info-value">
                      {paymentSession.token?.symbol}
                    </span>
                  </div>
                </div>

                {paymentSession.tokenAmount &&
                  (() => {
                    // Must be the amount this session was priced for, or the rate and
                    // the "$X = N TOKEN" line both describe a payment nobody is making.
                    const usdAmount = amountDueNow;
                    const tokenAmt = Number(paymentSession.tokenAmount) || 0;
                    const rate = tokenAmt > 0 ? usdAmount / tokenAmt : 0;
                    if (!rate) return null;
                    const sym = paymentSession.token?.symbol;
                    return (
                      <div className="rate-card">
                        <div className="rate-top">
                          <span className="rate-eq">
                            1 {sym} ≈ ${formatCrypto(rate, rate >= 1 ? 2 : 6)}
                          </span>
                          <span className="rate-live">
                            <span className="live-dot" />
                            Live market rate
                          </span>
                        </div>
                        <div className="rate-bottom">
                          ${formatUSD(usdAmount)} = {formatCrypto(tokenAmt)}{" "}
                          {sym} · rate locked for this session
                        </div>
                      </div>
                    );
                  })()}

                <div className="payment-tabs">
                  <button
                    className={`tab-btn ${activePaymentTab === "qr" ? "active" : ""}`}
                    onClick={() => setActivePaymentTab("qr")}
                  >
                    QR Code
                  </button>
                  <button
                    className={`tab-btn ${activePaymentTab === "wallet" ? "active" : ""}`}
                    onClick={() => setActivePaymentTab("wallet")}
                  >
                    Wallet Connect
                  </button>
                </div>

                <div className="tab-content">
                  {activePaymentTab === "qr" && (
                    <div className="qr-tab-content">
                      <div className="qr-code-box">
                        {qrCodeUrl ? (
                          <img src={qrCodeUrl} alt="Payment QR Code" />
                        ) : (
                          <div className="qr-placeholder">
                            Generating QR Code...
                          </div>
                        )}
                      </div>
                      <p className="qr-hint">Scan with your crypto wallet</p>

                      <div className="address-section">
                        <label>Send to this address:</label>
                        <div className="address-box">
                          <span className="address-text">
                            {paymentSession.receivingAddress}
                          </span>
                          <button
                            className="copy-btn"
                            onClick={handleCopyAddress}
                            title="Copy address"
                          >
                            {copied ? <Check size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                        {copied && <span className="copied-text">Copied!</span>}
                      </div>

                      <div className="amount-info">
                        <span className="amount-label">Amount:</span>
                        <span className="amount-value">
                          {formatCrypto(paymentSession.tokenAmount)}{" "}
                          {paymentSession.token?.symbol}
                        </span>
                      </div>
                    </div>
                  )}

                  {activePaymentTab === "wallet" && (
                    <div className="wallet-tab-content">
                      {isConnected ? (
                        <>
                          <p className="wallet-hint">
                            Connected: {walletAddress?.slice(0, 6)}...
                            {walletAddress?.slice(-4)}
                          </p>
                          <button
                            className="connect-wallet-btn"
                            onClick={handleWalletConnect}
                            disabled={isWalletProcessing || pendingTxHash}
                          >
                            {isWalletProcessing || pendingTxHash ? (
                              <>
                                <Loader2
                                  size={16}
                                  className="spinning"
                                  style={{ marginRight: "8px" }}
                                />
                                {pendingTxHash
                                  ? "Confirming..."
                                  : "Processing..."}
                              </>
                            ) : (
                              "Pay Now"
                            )}
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="wallet-hint">
                            Connect your wallet to pay directly
                          </p>
                          <button
                            className="connect-wallet-btn"
                            onClick={handleWalletConnect}
                          >
                            Connect Wallet
                          </button>
                        </>
                      )}

                      <div className="amount-info">
                        <span className="amount-label">Amount:</span>
                        <span className="amount-value">
                          {formatCrypto(paymentSession.tokenAmount)}{" "}
                          {paymentSession.token?.symbol}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {verifyMessage && (
                  <div className="verify-message">
                    <AlertCircle size={16} />
                    <span>{verifyMessage}</span>
                  </div>
                )}

                {/* For payment sessions: Hide "Change Selection" if any payment received */}
                {/* For invoices: Always show "Change Selection" */}
                {(!isSessionPayment || !paymentLink?.isPartiallyPaid) && (
                  <button
                    className="change-btn"
                    onClick={handleChangeSelection}
                    disabled={isReleasingSession}
                  >
                    {isReleasingSession ? "Releasing..." : "Change Selection"}
                  </button>
                )}
              </div>
            )}
            {paymentSession === null && sessionTimeRemaining === 0 && (
              <div className="payment-status-card expired">
                <XCircle size={48} />
                <h2>Payment Session Expired</h2>
                <p>Your payment session has timed out.</p>
              </div>
            )}

            <div className="payment-footer">
              <p className="pc-powered">
                <span>Powered by</span>
                <img src={imglogoDark} alt="MaxelPay" />
              </p>
            </div>
          </div>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  );
};

// Circumference of the r=54 status ring, so the dash maths lives in one place.
const PAY_RING_C = 2 * Math.PI * 54;

const styles = `
  .payment-page {
    min-height: 100vh;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .payment-container {
    width: 100%;
    max-width: 580px;
    max-height: calc(100vh - 2rem);
    display: flex;
    flex-direction: column;
  }

  .payment-card {
    background: white;
    border-radius: 20px;
    padding: 0;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }

  .payment-card.expanded {
    max-height: calc(100vh - 2rem);
    padding: 0;
  }

  .payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    background:linear-gradient(135deg, #8270ff 0%, #5847d6 100%);
  }
  .expanded span.merchant-brand-name {
    color: #fff;
  }
  .payment-header span.merchant-brand-name {
      color: #fff;
  }
  .payment-logo {
    font-size: 1.25rem;
    font-weight: 700;
    color:white;
    width:100%;
  }
    .payment-logo img {
      max-width: 180px;
    }

  /* Merchant branding sits directly on the white card (like a real checkout) */
  .merchant-brand {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0px;
  }

  .merchant-brand img {
    max-width: 200px;
    max-height: 60px;
    object-fit: contain;
  }

  .merchant-brand-name {
    font-family: 'Space Grotesk', 'Plus Jakarta Sans', sans-serif;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #1f2937;
    text-align: center;
  }

  .payment-amount-section {
    text-align: center;
    margin-bottom: 0.75rem;
  }

  .payment-amount {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    line-height: 1.1;
  }

  .payment-token-amount {
    font-size: 1rem;
    color: #7c3aed;
    font-weight: 600;
    margin-top: 0.25rem;
  }

  .payment-name {
    color: #6b7280;
    margin-top: 0.25rem;
    font-size: 0.9rem;
  }

  .partial-payment-banner {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #f59e0b;
    border-radius: 10px;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
  }

  .partial-badge {
    display: inline-block;
    background: #f59e0b;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    margin-bottom: 0.5rem;
  }

  .partial-progress {
    display: flex;
    justify-content: space-between;
    font-size: 0.9rem;
    color: #92400e;
    font-weight: 500;
  }

  .remaining-label {
    color: #f59e0b;
    font-size: 0.9rem;
    font-weight: 600;
    margin-top: 0.25rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .payment-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    position: relative;
  }

  .form-group label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
  }

  .form-group select {
    padding: 0.625rem 0.875rem;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 0.9rem;
    color: #1f2937;
    background: white;
    cursor: pointer;
    transition: border-color 0.2s;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 1rem;
    padding-right: 2.5rem;
    position: relative;
    z-index: 1;
  }

  .form-group select:focus {
    outline: none;
    border-color: #7c3aed;
  }

  .form-group select:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }

  .pay-now-btn {
    width: 100%;
    padding: 0.75rem;
    background: linear-gradient(135deg, #8270ff 0%, #5847d6 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: var(--font-display);
  }

  .pay-now-btn:hover:not(:disabled) {
    background: #6d28d9;
    transform: translateY(-1px);
  }

  .pay-now-btn:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    font-family: var(--font-display);
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .payment-options {
    border-top: 1px solid #e5e7eb;
    padding-top: 1rem;
    margin-top: 0.75rem;
  }

  /* Session Timer Banner */
  .session-timer-banner {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.875rem;
    background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
    border: 1px solid #93c5fd;
    border-radius: 10px;
    margin-bottom: 0.75rem;
  }

  .session-timer-banner.warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-color: #f59e0b;
  }

  .session-timer-banner.critical {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    border-color: #ef4444;
    animation: urgentPulse 1s ease-in-out infinite;
  }

  @keyframes urgentPulse {
    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
    50% { transform: scale(1.01); box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
  }

  .timer-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: white;
    border-radius: 50%;
    flex-shrink: 0;
    color: #3b82f6;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  .session-timer-banner.warning .timer-icon {
    color: #f59e0b;
  }

  .session-timer-banner.critical .timer-icon {
    color: #ef4444;
    animation: spin 2s linear infinite;
  }

  .timer-content {
    flex: 1;
  }

  .timer-countdown {
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    margin-bottom: 0.125rem;
  }

  .timer-value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #1e40af;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
    letter-spacing: 0.05em;
  }

  .session-timer-banner.warning .timer-value {
    color: #b45309;
  }

  .session-timer-banner.critical .timer-value {
    color: #dc2626;
  }

  .timer-label {
    font-size: 0.8rem;
    color: #6b7280;
    font-weight: 500;
  }

  .timer-message {
    margin: 0;
    font-size: 0.75rem;
    color: #4b5563;
    line-height: 1.3;
  }

  .session-timer-banner.warning .timer-message {
    color: #92400e;
  }

  .session-timer-banner.critical .timer-message {
    color: #991b1b;
    font-weight: 500;
  }

  .selected-info {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.75rem;
    background: #f3f4f6;
    border-radius: 8px;
  }

  .info-item {
    display: flex;
    gap: 0.375rem;
    font-size: 0.85rem;
  }

  .info-label {
    color: #6b7280;
  }

  .info-value {
    font-weight: 600;
    color: #1f2937;
  }

  /* Live conversion-rate breakdown */
  .rate-card {
    background: linear-gradient(180deg, #faf9ff, #f5f3ff);
    border: 1px solid #ece9ff;
    border-radius: 12px;
    padding: 0.7rem 0.9rem;
    margin-bottom: 0.75rem;
  }

  .rate-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .rate-eq {
    font-size: 0.95rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.01em;
  }

  .rate-live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #059669;
    white-space: nowrap;
  }

  .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.16);
    animation: ratePulse 1.8s ease-in-out infinite;
  }

  @keyframes ratePulse {
    0%, 100% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.18); }
    50% { box-shadow: 0 0 0 5px rgba(16, 185, 129, 0.06); }
  }

  .rate-bottom {
    margin-top: 5px;
    font-size: 0.76rem;
    color: #6b7280;
  }

  .payment-tabs {
    display: flex;
    background: #f3f4f6;
    border-radius: 10px;
    padding: 0.25rem;
    margin-bottom: 0.75rem;
  }

  .tab-btn {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: transparent;
    color: #6b7280;
    font-family: var(--iv-sans);
  }

  .tab-btn.active {
    background: white;
    color: #7c3aed;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  .tab-content {
    min-height: auto;
  }

  .qr-tab-content,
  .wallet-tab-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .qr-code-box {
    width: 160px;
    height: 160px;
    background: #f9fafb;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.5rem;
    overflow: hidden;
  }

  .qr-code-box img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .qr-placeholder {
    color: #9ca3af;
    font-size: 0.85rem;
  }

  .qr-hint,
  .wallet-hint {
    color: #6b7280;
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }

  .address-section {
    width: 100%;
    margin-top: 0.5rem;
  }

  .address-section label {
    display: block;
    font-size: 0.8rem;
    color: #6b7280;
    margin-bottom: 0.375rem;
  }

  .address-box {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .address-text {
    flex: 1;
    font-family: monospace;
    font-size: 0.7rem;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .copy-btn {
    padding: 0.375rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .copy-btn:hover {
    border-color: #7c3aed;
    color: #ffffff;
  }

  .copy-btn.copied {
    background: #10b981;
    border-color: #10b981;
    color: white;
  }

  .copied-text {
    display: block;
    font-size: 0.75rem;
    color: #10b981;
    margin-top: 0.25rem;
    font-weight: 500;
    animation: fadeIn 0.2s ease-out;
  }

  .amount-info {
    display: flex;
    justify-content: center;
    gap: 0.375rem;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f0fdf4;
    border-radius: 8px;
    width: 100%;
  }

  .amount-label {
    color: #6b7280;
    font-size: 0.85rem;
  }

  .amount-value {
    font-weight: 600;
    color: #059669;
    font-size: 0.85rem;
  }

  .connect-wallet-btn {
    width: 100%;
    padding: 0.75rem;
    background: #7c3aed;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 0.5rem;
  }

  .connect-wallet-btn:hover {
    background: #6d28d9;
  }

  .change-btn {
    width: 100%;
    padding: 0.5rem;
    background: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 0.75rem;
    font-family: var(--iv-sans);
  }

  .change-btn:hover:not(:disabled) {
    background: #f9fafb;
    color: #374151;
  }

  .change-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .session-expired {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 8px;
    font-size: 0.85rem;
    margin-top: 0.75rem;
  }

  .payment-footer {
    text-align: center;
    margin-top: 0.75rem;
    padding-top: 0.5rem;
    border-top: 1px solid #f3f4f6;
  }

  .payment-footer p {
    color: #9ca3af;
    font-size: 0.75rem;
    margin: 0;
  }

  .payment-footer strong {
    color: #7c3aed;
  }

  /* Loading & Status States */
  .payment-loading {
    text-align: center;
    padding: 4rem;
    background: white;
    border-radius: 24px;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e5e7eb;
    border-top-color: #7c3aed;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1.5rem;
  }

  .payment-loading p {
    font-size: 1.1rem;
    color: #6b7280;
  }

  .payment-status-card {
    text-align: center;
    padding: 4rem 3rem;
    background: white;
    border-radius: 24px;
  }

  .payment-status-card svg {
    margin-bottom: 1.5rem;
  }

  .payment-status-card.success svg {
    color: #10b981;
  }

  .payment-status-card.error svg {
    color: #ef4444;
  }

  .payment-status-card.expired svg,
  .payment-status-card.inactive svg {
    color: #6b7280;
  }

  .payment-status-card h2 {
    font-size: 1.75rem;
    color: #1f2937;
    margin: 0 0 0.75rem;
  }

  .payment-status-card p {
    color: #6b7280;
    margin: 0;
    font-size: 1.1rem;
  }

  .status-date,
  .status-hint {
    font-size: 1rem;
    margin-top: 0.75rem !important;
  }

  .go-back-btn {
    margin-top: 1.5rem;
    padding: 0.875rem 2rem;
    background: #7c3aed;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .go-back-btn:hover {
    background: #6d28d9;
  }

  /* Payment Success Overlay */
  .payment-success-overlay {
    text-align: center;
    padding: 3rem 2.5rem;
    background: white;
    border-radius: 24px;
    animation: fadeInScale 0.5s ease-out;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  }

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .success-animation {
    margin-bottom: 1.5rem;
  }

  .success-animation svg {
    color: #10b981;
    animation: successPop 0.6s ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    .pay-mark__glyph,
    .pay-mark__fill,
    .confirmation-card.pending .confirmation-progress-bar::after {
      animation: none;
    }

    .pay-mark__fill,
    .confirmation-progress-fill {
      transition: none;
    }
  }

  /* Status mark: a progress ring around the glyph. Replaces .confirming-icon and
     .confirming-spinner, where a 24px spinner pinned to the icon's bottom-right
     corner read as a rendering artefact rather than an indicator. */
  .pay-mark {
    position: relative;
    width: 104px;
    height: 104px;
    margin: 0 auto;
    display: grid;
    place-items: center;
  }

  .pay-mark__ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    /* -90deg puts 0% at twelve o'clock; without it the sweep starts at three. */
    transform: rotate(-90deg);
    overflow: visible;
  }

  .pay-mark__track {
    fill: none;
    stroke: #eef0f4;
    stroke-width: 7;
  }

  .pay-mark__fill {
    fill: none;
    stroke: currentColor;
    stroke-width: 7;
    stroke-linecap: round;
    transition: stroke-dashoffset 700ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .pay-mark__glyph {
    display: grid;
    place-items: center;
    animation: pay-glyph-in 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
  }

  @keyframes pay-glyph-in {
    from { opacity: 0; transform: scale(0.82); }
    to   { opacity: 1; transform: none; }
  }

  /* One colour per state. The ring picks it up through currentColor and the
     glyph is set from the same rule, so ring and glyph cannot disagree. */
  .pay-mark--ok { color: #10b981; }
  .pay-mark--pending { color: #7c3aed; }
  .pay-mark--partial { color: #6366f1; }
  .pay-mark--warn { color: #f59e0b; }

  .pay-mark--ok .pay-mark__glyph svg { color: #10b981; }
  .pay-mark--pending .pay-mark__glyph svg { color: #7c3aed; }
  .pay-mark--partial .pay-mark__glyph svg { color: #6366f1; }
  .pay-mark--warn .pay-mark__glyph svg { color: #f59e0b; }

  /* While unconfirmed the ring breathes, so a payment sitting at 0 of 62 still
     looks alive rather than stalled. */
  .pay-mark--pending .pay-mark__fill,
  .pay-mark--partial .pay-mark__fill,
  .pay-mark--warn .pay-mark__fill {
    animation: pay-ring-breathe 2.4s ease-in-out infinite;
  }

  @keyframes pay-ring-breathe {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }

  /* Headline colour follows the state. It was green unconditionally, so a short
     or unsupported payment was rendered in the success colour. */
  .pay-headline--ok { color: #10b981; }
  .pay-headline--pending { color: #7c3aed; }
  .pay-headline--partial { color: #4f46e5; }
  .pay-headline--warn { color: #b45309; }

  @keyframes successPop {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .payment-success-overlay h2 {
    font-size: 1.85rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    /* Deliberately no colour: .pay-headline--* supplies it per state. */
    margin: 0 0 0.75rem;
  }

  .success-amount {
    font-size: 1.5rem;
    color: #1f2937;
    font-weight: 700;
    margin: 0 0 0.25rem;
  }

  .success-token-amount {
    font-size: 1.1rem;
    color: #7c3aed;
    font-weight: 600;
    margin: 0 0 1.5rem;
  }

  /* Block Confirmation Card */
  .confirmation-card {
    width: 100%;
    max-width: 100%;
    padding: 1rem 1.25rem;
    border-radius: 12px;
    margin-bottom: 1rem;
    text-align: left;
  }

  .confirmation-card.pending {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #f59e0b;
    max-width: 100%;
  }

  .confirmation-card.confirmed {
    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
    border: 1px solid #10b981;
    max-width: 100%;
  }

  .confirmation-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .confirmation-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confirmation-card.pending .confirmation-icon {
    color: #d97706;
  }

  .confirmation-card.confirmed .confirmation-icon {
    color: #059669;
  }

  .confirmation-title {
    font-weight: 600;
    font-size: 0.95rem;
  }

  .confirmation-card.pending .confirmation-title {
    color: #92400e;
  }

  .confirmation-card.confirmed .confirmation-title {
    color: #047857;
  }

  .confirmation-progress-bar {
    position: relative;
    width: 100%;
    height: 8px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 100px;
    overflow: hidden;
    margin-bottom: 0.5rem;
  }

  /* Indeterminate sweep behind the fill. At 0 of 62 the bar was a plain empty
     track, which reads as broken rather than waiting. */
  .confirmation-card.pending .confirmation-progress-bar::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 100px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(245, 158, 11, 0.45) 50%,
      transparent 100%
    );
    animation: pay-bar-sweep 1.6s ease-in-out infinite;
  }

  @keyframes pay-bar-sweep {
    from { transform: translateX(-100%); }
    to   { transform: translateX(100%); }
  }

  .confirmation-progress-fill {
    /* Above the sweep, so real progress always reads over the placeholder. */
    position: relative;
    z-index: 1;
    height: 100%;
    background: linear-gradient(90deg, #f59e0b, #eab308);
    border-radius: 100px;
    transition: width 700ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .confirmation-counts {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.85rem;
    color: #92400e;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .confirmation-pct {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .confirmation-message {
    font-size: 0.8rem;
    color: #78350f;
    margin: 0;
    line-height: 1.4;
  }

  .confirmation-message.success {
    color: #047857;
  }

  .success-message.verifying {
    background: #fef3c7;
    color: #92400e;
  }

  .transactions-list {
    width: 100%;
    max-width: 400px;
    margin-bottom: 1.5rem;
  }

  .transactions-list > .tx-label {
    display: block;
    text-align: center;
    margin-bottom: 0.75rem;
  }

  .tx-hash-box {
    background: #f3f4f6;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    margin-bottom: 0.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .transactions-list .tx-hash-box:last-child {
    margin-bottom: 0;
  }

  .tx-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    justify-content: center;
  }

  .tx-index {
    font-size: 0.75rem;
    color: #9ca3af;
    font-weight: 600;
  }

  .tx-amount {
    font-size: 0.9rem;
    color: #059669;
    font-weight: 600;
  }

  .tx-label {
    font-size: 0.8rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .tx-hash {
    font-family: monospace;
    font-size: 0.85rem;
    color: #374151;
    margin: 0;
    word-break: break-all;
  }

  .copy-tx-btn {
    padding: 0.5rem 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #6b7280;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    transition: all 0.2s;
  }

  .copy-tx-btn:hover {
    border-color: #7c3aed;
    color: #7c3aed;
  }

  .success-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.25rem;
    background: #d1fae5;
    color: #059669;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 500;
    margin-bottom: 1rem;
  }

  .success-note {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0 0 1.5rem;
  }

  .payment-status-badge {
    display: inline-block;
    padding: 0.5rem 1.25rem;
    border-radius: 50px;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 1rem 0;
  }

  .payment-status-badge.completed {
    background: #d1fae5;
    color: #059669;
  }

  .payment-status-badge.partial {
    background: #e0e7ff;
    color: #4f46e5;
  }

  .payment-status-badge.overpaid {
    background: #dbeafe;
    color: #2563eb;
  }

  .payment-status-badge.unsupported_token {
    background: #fee2e2;
    color: #dc2626;
  }

  .unsupported-token-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    margin: 1rem 0;
    text-align: left;
  }

  .unsupported-token-warning svg {
    flex-shrink: 0;
    color: #dc2626;
    margin-top: 2px;
  }

  .unsupported-token-warning strong {
    display: block;
    margin-bottom: 0.25rem;
    color: #dc2626;
  }

  .unsupported-token-warning p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.4;
    color: #7f1d1d;
  }

  .remaining-info,
  .overpaid-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-radius: 10px;
    font-size: 1rem;
    margin-bottom: 1rem;
  }

  .remaining-info {
    background: #fef3c7;
    color: #92400e;
  }

  .remaining-info strong {
    color: #d97706;
  }

  .overpaid-info {
    background: #dbeafe;
    color: #1e40af;
  }

  .overpaid-info strong {
    color: #2563eb;
  }

  .pay-remaining-btn {
    width: 100%;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 0.75rem;
    box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
  }

  .pay-remaining-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(124, 58, 237, 0.4);
  }

  /* I've Paid / Verify Button */
  .verify-payment-btn {
    width: 100%;
    padding: 1rem;
    background: #10b981;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .verify-payment-btn:hover:not(:disabled) {
    background: #059669;
    transform: translateY(-1px);
  }

  .verify-payment-btn:disabled {
    background: #6ee7b7;
    cursor: not-allowed;
  }

  /* Verification message */
  .verify-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #fef3c7;
    color: #92400e;
    border-radius: 8px;
    font-size: 0.9rem;
    margin-top: 0.75rem;
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Checking payment indicator */
  .checking-payment {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background: #f3f4f6;
    color: #6b7280;
    border-radius: 8px;
    font-size: 0.8rem;
    margin-top: 0.75rem;
  }

  .checking-payment svg {
    animation: spin 1.5s linear infinite;
  }

  @media (max-width: 620px) {
    .payment-container {
      max-width: 100%;
    }

    .payment-card {
      padding: 1rem;
      border-radius: 16px;
    }

    .payment-card.expanded {
      padding: 0.75rem;
    }

    .payment-amount {
      font-size: 2rem;
    }

    .qr-code-box {
      width: 140px;
      height: 140px;
    }

    .selected-info {
      flex-direction: row;
      gap: 1rem;
    }

    .form-group select {
      font-size: 0.85rem;
      padding: 0.5rem 0.625rem;
    }

    .tab-btn {
      padding: 0.375rem 0.5rem;
      font-size: 0.8rem;
    }

    /* Session Timer Banner Mobile */
    .session-timer-banner {
      gap: 0.5rem;
      padding: 0.5rem 0.625rem;
    }

    .timer-icon {
      width: 32px;
      height: 32px;
    }

    .timer-countdown {
      gap: 0.25rem;
    }

    .timer-value {
      font-size: 1.15rem;
    }

    .timer-label {
      font-size: 0.7rem;
    }

    .timer-message {
      font-size: 0.7rem;
    }

    .address-text {
      font-size: 0.6rem;
    }
  }

  

  /* ══════════════════════════════════════════════════════════════════════
     Checkout header, brand and footer -- matched to the public invoice view:
     same MaxelPay violet, same Poppins, same decorative treatment.
     ══════════════════════════════════════════════════════════════════════ */

  .payment-header {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #5b3fd6 0%, #7c5cff 52%, #4f46e5 100%);
    padding: 2.35rem 1.6rem;
    display: block;
    min-height: 0;
  }

  .pc-wave {
    position: absolute;
    top: -60%;
    right: -20%;
    width: 72%;
    height: 220%;
    z-index: 0;
    border-radius: 46% 54% 42% 58% / 52% 44% 56% 48%;
    background: linear-gradient(
      140deg,
      rgba(255, 255, 255, 0.16) 0%,
      rgba(255, 255, 255, 0.05) 48%,
      transparent 78%
    );
    animation: pc-wave-drift 14s ease-in-out infinite alternate;
  }

  @keyframes pc-wave-drift {
    from { transform: translate3d(0, 0, 0) rotate(0deg); }
    to   { transform: translate3d(-4%, 3%, 0) rotate(7deg); }
  }

  .pc-orb {
    position: absolute;
    top: 26%;
    right: 14%;
    width: 30px;
    height: 30px;
    z-index: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.26);
  }

  .pc-brand {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    min-width: 0;
  }

  /* Height-constrained with width: auto, so the 604.5 x 125 lockup keeps its
     aspect ratio. Setting a width instead would squash it. */
  .pc-brand__logo {
    // height: 26px;
    width: auto;
    display: block;
    max-width:170px;
  }

  .pc-brand__tag {
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.76rem;
    font-weight: 400;
    letter-spacing: 0.01em;
  }

  /* The white-on-transparent mark, so it needs no tile against the gradient.
     Sized explicitly: the source is 37x32, and letting it render at intrinsic
     size next to 1.18rem text left it visually undersized. */




  /* Who the payer is actually paying. The header carries MaxelPay now, so this
     is the only place the merchant is named. */
  .pc-billed-by {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: #ffffff;
  }

  .pc-billed-by strong {
    color: #ffffff;
    // font-weight: 600;
  }

  /* ── Proceed to Pay: the invoice view's pill button ── */

  .pay-now-btn {
    border-radius: 999px;
    font-family: inherit;
    background: linear-gradient(135deg, #7c5cff 0%, #5b3fd6 100%);
    transition:
      transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1),
      box-shadow 180ms cubic-bezier(0.22, 0.61, 0.36, 1),
      opacity 180ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .pay-now-btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(91, 63, 214, 0.38);
  }

  /* Disabled is the state a payer sees first, before picking a network, so it
     has to look deliberately inert rather than broken. */
  .pay-now-btn:disabled {
    background: #e6e4f0;
    color: #9b98ae;
    box-shadow: none;
    transform: none;
    cursor: not-allowed;
  }

  /* ── Footer: the mark, not the word ── */

  .pc-powered {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.82rem;
    color: #9ca3af;
  }

  .pc-powered img {
    height: 18px;
    width: auto;
    /* Optical alignment: the wordmark's baseline sits slightly high next to
       the label at this size. */
    margin-top: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    .pc-wave { animation: none; }
    .pay-now-btn:not(:disabled):hover { transform: none; }
  }

`;

export default PaymentPage;
